// ─────────────────────────────────────────────────────────────────────────────
//  AutoReports — Frontend
//  Stack: React + JWT Auth + PostgreSQL (via server) + Redis (via server)
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useRef, useCallback } from "react";

// Empty string = relative URLs → CRA proxy forwards /api/* to localhost:4000
// Set REACT_APP_SERVER_URL only when deploying to a separate domain
const SERVER = process.env.REACT_APP_SERVER_URL || "";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "740129068318-732iifgvse6d9sm578co7dq993uc700s.apps.googleusercontent.com";

// JWT stored in localStorage (survives refresh, cleared on logout)
// Token itself is NOT the ClickUp token — it's a short JWT issued by your server
const JWT_KEY     = "cu_autoreports_jwt";
const CLICKUP_KEY = "cu_autoreports_ctoken"; // ClickUp token in sessionStorage only

function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }

    const existing = document.querySelector('script[data-google-gis="true"]');
    if (existing) {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.dataset.googleGis = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google sign-in script"));
    document.head.appendChild(script);
  });
}

const TYPE_META = {
  Space:  { icon:"🌐", color:"#4B9EFF", bg:"rgba(75,158,255,.12)", label:"All folders and projects within — team-wide reporting." },
  Folder: { icon:"📁", color:"#4ECDC4", bg:"rgba(78,205,196,.12)", label:"All projects within this folder — workstream reporting." },
  List:   { icon:"📋", color:"#4ECDC4", bg:"rgba(78,205,196,.12)", label:"Single list — focused, list-specific task reporting." },
};

const METRICS = [
  { id:"total",           name:"Total tasks",         desc:"Total number of tasks and subtasks created within the project, helping understand overall project scope and workload.", icon:"📊" },
  { id:"done",            name:"Completed tasks",      desc:"Number of tasks with status marked as Done, Completed, or Closed, providing visibility into delivered work and project progress.", icon:"✅" },
  { id:"inprogress",      name:"In progress",          desc:"Number of tasks currently being worked on and marked as In Progress, helping track active efforts and team focus.", icon:"⚡" },
  { id:"todo",            name:"To do",                desc:"Number of tasks that are planned but have not yet been started, enabling better planning of upcoming work.", icon:"🔲" },
  { id:"overdue",         name:"Overdue tasks",        desc:"Tasks whose due date has passed and are not yet marked as Completed, Done, or Closed, helping identify schedule risks and potential delays.", icon:"🔴" },
  { id:"blocked",         name:"Blocked tasks",        desc:"Tasks that cannot proceed due to dependencies, issues, or external constraints and are marked as Blocked, highlighting impediments that require attention.", icon:"🚫" },
  { id:"onhold",          name:"On hold tasks",        desc:"Tasks temporarily paused and marked as On Hold, providing visibility into deferred work and its impact on timelines.", icon:"⏸" },
  { id:"withoutdue",      name:"Tasks without due date", desc:"Tasks that do not have a target completion date assigned, helping identify planning gaps and improve schedule management.", icon:"📅" },
  { id:"withoutassignee", name:"Tasks without assignee", desc:"Tasks that are currently unassigned and have no designated owner, ensuring accountability and preventing work from being overlooked.", icon:"👤" },
  { id:"estimated",       name:"Estimated effort (hours)", desc:"Total planned effort across all tasks based on estimated hours, supporting resource planning and workload forecasting.", icon:"📐" },
  { id:"tracked",         name:"Logged effort (hours)", desc:"Total actual effort recorded against tasks, enabling tracking of resource utilization and project execution.", icon:"⏱" },
  { id:"remaining",       name:"Remaining effort (hours)", desc:"Remaining work calculated as Estimated Effort - Logged Effort, helping assess outstanding work and expected completion timelines.", icon:"⏳" },
  { id:"assignee",        name:"Assignee breakdown",   desc:"Task distribution by team member, providing insight into workload balance and resource allocation.", icon:"👥" },
  { id:"status",          name:"Status breakdown",     desc:"Distribution of tasks across all project statuses, providing visibility into overall project progress and bottlenecks.", icon:"📈" },
  { id:"priority",        name:"Priority breakdown",   desc:"Task distribution by priority level, helping identify workload, focus areas, and critical tasks requiring immediate attention.", icon:"🎯" },
  { id:"completion",      name:"Project completion percentage", desc:"Overall project progress based on completed work, providing a clear measure of project health and delivery readiness.", icon:"🏁" },
];

const DAYS        = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const TIMEZONES   = ["Asia/Kolkata","UTC","America/New_York","America/Los_Angeles","Europe/London","Asia/Singapore"];
const STEP_LABELS = ["Scope","Metrics","Preview","Channel","Schedule"];
const STEP_ICONS  = ["🔍","📊","👁","📣","📅"];
const BAR_COLORS  = ["#5B8EFF","#4ECDC4","#FFD166","#FF6B6B","#A78BFA","#34D399"];

function deliveryLabel(item) {
  return item?.channelName || item?.channel_name || item?.channel || "";
}

function reportScopes(state) {
  return Array.isArray(state?.scopeItems) && state.scopeItems.length
    ? state.scopeItems
    : state?.scopeItem ? [state.scopeItem] : [];
}

function scopeSummary(state) {
  const scopes = reportScopes(state);
  if (!scopes.length) return "Not selected";
  if (scopes.length === 1) {
    const item = scopes[0];
    return `${item.type==="List"?"List":item.type}: ${item.name}`;
  }
  return `${scopes.length} list reports`;
}

function itemSpaceId(item, tree = []) {
  if (!item) return "";
  if (item.type === "Space") return String(item.id);
  const byId = new Map(tree.map(i => [String(i.id), i]));
  let current = item;
  while (current?.parent) {
    const parent = byId.get(String(current.parent));
    if (!parent) break;
    if (parent.type === "Space") return String(parent.id);
    current = parent;
  }
  const pathSpace = String(item.path || "").split(/\s*[›>]\s*/)[0]?.trim();
  const space = tree.find(i => i.type === "Space" && i.name === pathSpace);
  return space ? String(space.id) : "";
}

function appendDeliveryTarget(currentChannel, currentName, value, label) {
  const raw = String(currentChannel || "");
  const parts = raw.split(/[,;\n]/);
  const trimmed = parts.map(p => p.trim()).filter(Boolean);
  const endsWithDelimiter = /[,;\n]\s*$/.test(raw);
  const nameParts = String(currentName || "")
    .split(/[,;\n]/)
    .map(p => p.trim())
    .filter(Boolean);

  let next = [];
  let nextNames = [];
  if (endsWithDelimiter || trimmed.length === 0) {
    next = trimmed;
    nextNames = nameParts.slice(0, next.length);
  } else {
    next = trimmed.slice(0, -1);
    nextNames = nameParts.slice(0, next.length);
  }

  if (!next.includes(value)) {
    next.push(value);
    nextNames.push(label);
  }

  return { channel: next.join(", "), channelName: nextNames.join(", ") };
}

function reportDays(report) {
  if (Array.isArray(report?.days)) return report.days.filter(d => DAYS.includes(d));
  return String(report?.days || "")
    .split(/[,;{}\s]+/)
    .map(d => d.trim())
    .filter(d => DAYS.includes(d));
}

function reportTime(report) {
  const value = String(report?.time || "09:00");
  return /^\d{2}:\d{2}$/.test(value) ? value : "09:00";
}

function daysSince(dateValue) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000));
}

function daysSinceLabel(dateValue) {
  const days = daysSince(dateValue);
  if (days == null) return "Unknown";
  if (days === 0) return "Today";
  return `${days} day${days === 1 ? "" : "s"}`;
}

function formatAdminDate(dateValue) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return `${date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} (${date.toLocaleDateString("en-IN", { weekday: "short" })})`;
}

function elapsedLabel(dateValue) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "Unknown";
  const totalMins = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  const days = Math.floor(totalMins / 1440);
  const hours = Math.floor((totalMins % 1440) / 60);
  const mins = totalMins % 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours || parts.length) parts.push(`${hours}h`);
  if (!parts.length || mins) parts.push(`${mins}m`);
  return parts.join(" ");
}

// Demo data shown when user tries demo mode (no real token)
const DEMO_DATA = {
  taskCount: 41,
  metrics: { total:41,done:21,inprogress:8,todo:12,overdue:5,blocked:2,onhold:1,withoutDueDate:7,withoutAssignee:4,completion:51,estimatedH:120,trackedH:68,remainingH:52 },
  assignees:  [{name:"Rahul",count:12},{name:"Priya",count:8},{name:"John",count:5}],
  statuses:   [{name:"In Progress",count:8},{name:"To Do",count:12},{name:"Done",count:21}],
  priorities: {urgent:4,high:11,normal:20,low:6,none:0},
  overdueTasks: [
    { id:"demo-101", name:"Finalize weekly QA checklist", url:"https://app.clickup.com/t/demo-101" },
    { id:"demo-102", name:"Review client feedback", url:"https://app.clickup.com/t/demo-102" },
    { id:"demo-103", name:"Update rollout notes", url:"https://app.clickup.com/t/demo-103" },
    { id:"demo-104", name:"Resolve open blocker on dashboard", url:"https://app.clickup.com/t/demo-104" },
    { id:"demo-105", name:"Confirm delivery timeline", url:"https://app.clickup.com/t/demo-105" },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
//  API HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Make authenticated request to your server
// Automatically adds JWT + ClickUp token headers
async function apiRequest(path, options = {}, clickupToken = null) {
  const jwt = localStorage.getItem(JWT_KEY);
  const headers = {
    "Content-Type": "application/json",
    ...(jwt          && { "Authorization":    `Bearer ${jwt}` }),
    ...(clickupToken && { "x-clickup-token":  clickupToken }),
    ...options.headers,
  };
  const res = await fetch(`${SERVER}${path}`, { ...options, headers });
  return res;
}

// Get task metrics — goes through Redis → PostgreSQL → ClickUp
async function fetchTaskData(type, id, workspaceId, clickupToken, fresh = false) {
  try {
    const qs = fresh ? "?fresh=1" : "";
    const res  = await apiRequest(`/api/tasks/${type}/${id}${qs}`, {}, clickupToken);
    const data = await res.json();
    if (data.error) return { error: data.error };

    return {
      taskCount: data.taskCount,
      metrics: {
        ...data.metrics,
        estimatedTime: data.metrics.estimatedMinutes ?? Math.round((data.metrics.estimatedH || 0) * 60),
        trackedTime:   data.metrics.trackedMinutes   ?? Math.round((data.metrics.trackedH   || 0) * 60),
        remainingTime: data.metrics.remainingMinutes ?? Math.round((data.metrics.remainingH || 0) * 60),
      },
      assignees:  data.assignees  || [],
      statuses:   data.statuses   || [],
      priorities: Object.entries(data.priorities || {}).map(([priority,count])=>({priority,count})),
      overdueTasks: data.overdueTasks || [],
      taskDetails: data.taskDetails || [],
    };
  } catch {
    return { error: `Cannot reach server at ${SERVER}` };
  } 
}

async function fetchSlackChannels(search = "") {
  const qs = search ? `?q=${encodeURIComponent(search)}` : "";
  const res = await apiRequest(`/api/slack/channels${qs}`);
  const data = await res.json();
  if (data.error) return { error: data.error };
  return { channels: data.channels || [] };
}

async function fetchTeamsChannels(search = "", refresh = false) {
  const params = new URLSearchParams();
  if (search) params.set("q", search);
  if (refresh) params.set("refresh", "1");
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await apiRequest(`/api/teams/channels${qs}`);
  const data = await res.json();
  if (data.error) return { error: data.error };
  return { groups: data.groups || [], channels: data.channels || [], warning: data.warning || "" };
}

function formatMinutes(mins) {
  if (!mins || mins===0) return "0h";
  const sign=mins<0?"-":"", abs=Math.abs(mins), h=Math.floor(abs/60), m=abs%60;
  return `${sign}${h}h${m>0?` ${m}m`:""}`;
}

// ─────────────────────────────────────────────────────────────────────────────
//  CSS — Brand Design (Purple #4C1D95 · Lime #BEF264 · Slate · Crimson)
// ─────────────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.cu*,.cu *::before,.cu *::after{box-sizing:border-box;margin:0;padding:0}
.cu{font-family:'Inter',sans-serif;background:#F8FAFC;color:#1E1B4B;min-height:100vh;
  --p:#4C1D95;--p-dark:#3B0764;--p2:#EDE9FE;--p2-dark:#4C1D95;
  --slate:#64748B;--slate-light:#E2E8F0;
  --red:#EF4444;--red-light:#FEE2E2;
  --surf:#FFFFFF;--card:#F1F5F9;--card2:#E2E8F0;
  --t:#1E1B4B;--tm:rgba(30,27,75,.7);--th:#64748B;
  --b:rgba(76,29,149,.08);--bh:rgba(76,29,149,.2);
  --amber:#D97706;--blue:#3B82F6;--green:#22C55E}
.cu.light{background:#F8FAFC;color:#1E1B4B;--p:#4C1D95;--p-dark:#3B0764;--p2:#EDE9FE;--p2-dark:#4C1D95;--slate:#64748B;--slate-light:#E2E8F0;--red:#EF4444;--red-light:#FEE2E2;--surf:#FFFFFF;--card:#F1F5F9;--card2:#E2E8F0;--t:#1E1B4B;--tm:rgba(30,27,75,.7);--th:#64748B;--b:rgba(76,29,149,.08);--bh:rgba(76,29,149,.2);--amber:#D97706;--blue:#3B82F6;--green:#22C55E}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
.anim{animation:fadeUp .25s ease}
.spinner{width:16px;height:16px;border:2px solid rgba(0,0,0,.1);border-top-color:var(--p);border-radius:50%;animation:spin .65s linear infinite;flex-shrink:0}
.spinner.sm{width:13px;height:13px}.spinner.wh{border-top-color:#fff}
.topbar{background:var(--surf);border-bottom:2px solid var(--p);height:62px;padding:0 24px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:100;position:relative}
.tl{display:flex;align-items:center;gap:10px;min-width:160px}
.tcenter{position:absolute;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:10px}
.tname{font-size:24px;font-weight:800;letter-spacing:-.8px;color:var(--p)}
.twsname{font-size:12px;color:var(--p-dark);font-weight:600;background:var(--p2);border:1px solid rgba(76,29,149,.25);border-radius:6px;padding:3px 10px}
.tright{display:flex;align-items:center;gap:8px;min-width:160px;justify-content:flex-end}
.ws-badge{font-size:11px;padding:5px 12px;border-radius:6px;font-weight:600;display:flex;align-items:center;gap:6px;border:1px solid}
.ws-badge.ok{background:rgba(34,197,94,.08);color:#16a34a;border-color:rgba(34,197,94,.2)}
.ws-badge.no{background:var(--red-light);color:var(--red);border-color:var(--red)}
.new-btn{padding:8px 18px;border-radius:7px;background:var(--p);color:#fff;font-size:12px;font-weight:600;border:none;cursor:pointer;font-family:'Inter',sans-serif;transition:all .15s}
.new-btn:hover{background:var(--p-dark)}
.disc-btn{padding:8px 16px;border-radius:7px;background:var(--red-light);color:var(--red);font-size:12px;font-weight:600;border:1px solid var(--red);cursor:pointer;font-family:'Inter',sans-serif;transition:all .15s}
.disc-btn:hover{background:var(--red);color:#fff}
.tabs{background:var(--surf);border-bottom:1px solid var(--card2);padding:0 24px;display:flex}
.tab{padding:14px 16px;font-size:12px;font-weight:600;cursor:pointer;color:var(--slate);border:none;background:transparent;border-bottom:2px solid transparent;margin-bottom:-1px;transition:all .15s;font-family:'Inter',sans-serif;display:flex;align-items:center;gap:6px}
.tab:hover{color:var(--p)}.tab.on{color:var(--p);border-bottom-color:var(--p)}.tab:disabled{opacity:.3;cursor:not-allowed}
.tcnt{background:rgba(76,29,149,.1);color:var(--p);font-size:10px;padding:1px 6px;border-radius:10px;font-weight:700}
.layout{display:grid;grid-template-columns:265px 1fr;min-height:calc(100vh - 62px)}
.sidebar{background:var(--surf);border-right:1px solid var(--card2);display:flex;flex-direction:column}
.sib-top{padding:18px 16px;border-bottom:1px solid var(--card2)}
.sib-ws-box{background:var(--card);border:1px solid var(--b);border-radius:10px;padding:12px 13px;display:flex;align-items:center;gap:10px}
.sib-ws-ico{width:34px;height:34px;border-radius:8px;background:linear-gradient(135deg,var(--p),var(--slate));display:flex;align-items:center;justify-content:center;font-size:15px;color:#fff;flex-shrink:0}
.sib-ws-label{font-size:10px;color:var(--th);font-weight:600;letter-spacing:.6px;text-transform:uppercase;margin-bottom:2px}
.sib-ws-name{font-size:13px;font-weight:700;color:var(--t);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:150px}
.sib-ws-check{font-size:10px;color:var(--p-dark);background:var(--p2);border:1px solid rgba(0,0,0,.15);border-radius:5px;padding:2px 7px;font-weight:700;flex-shrink:0}
.sib-nav{padding:14px 12px;flex:1}
.sib-nav-ttl{font-size:10px;font-weight:700;letter-spacing:1px;color:var(--th);text-transform:uppercase;margin-bottom:8px;padding:0 8px}
.srow{display:flex;align-items:center;gap:11px;padding:10px 11px;border-radius:9px;margin-bottom:3px;cursor:default;transition:all .15s}
.srow.on{background:rgba(76,29,149,.06);border-left:4px solid var(--p);padding-left:8px;border-radius:0 9px 9px 0}
.snum{width:28px;height:28px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;transition:all .2s}
.snum.done{background:var(--p2);color:var(--p-dark)}.snum.act{background:rgba(76,29,149,.15);color:var(--p)}.snum.idle{background:var(--slate-light);color:var(--slate)}
.srow-text{flex:1;min-width:0}
.slabel{font-size:13px;font-weight:600;color:var(--t)}.slabel.idle{color:var(--slate);font-weight:500}
.ssub{font-size:10px;color:var(--th);margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.sdiv{height:1px;background:var(--card2);margin:10px 12px}
.sib-bottom{padding:12px 16px}
.feedback-card{background:linear-gradient(180deg, rgba(76,29,149,.04), rgba(255,255,255,.75));border:1px solid rgba(76,29,149,.12);border-radius:14px;padding:14px}
.feedback-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:8px}
.feedback-title{font-size:12px;font-weight:800;color:var(--t)}
.feedback-sub{font-size:10px;color:var(--th);white-space:nowrap}
.feedback-flow{display:flex;flex-wrap:wrap;gap:5px;align-items:center;font-size:10px;color:var(--p-dark);background:rgba(76,29,149,.06);border:1px dashed rgba(76,29,149,.18);border-radius:9px;padding:7px 8px;margin-bottom:8px}
.feedback-flow span{display:inline-flex;align-items:center;gap:4px}
.feedback-ta{width:100%;resize:none;background:var(--surf);border:1.5px solid var(--card2);border-radius:10px;padding:9px 10px;font-size:12px;color:var(--t);font-family:'Inter',sans-serif;outline:none;transition:all .15s;line-height:1.45}
.feedback-ta:focus{border-color:var(--p);box-shadow:0 0 0 3px rgba(76,29,149,.08)}
.feedback-err,.feedback-ok{font-size:10px;line-height:1.45;margin-top:6px}
.feedback-err{color:var(--red)}
.feedback-ok{color:#16a34a}
.feedback-actions{display:flex;justify-content:flex-end;margin-top:8px}
.feedback-btn{padding:7px 12px;border-radius:8px;background:var(--p);color:#fff;font-size:11px;font-weight:700;border:none;cursor:pointer;font-family:'Inter',sans-serif;transition:all .15s}
.feedback-btn:hover{background:var(--p-dark)}
.feedback-btn:disabled{opacity:.55;cursor:not-allowed}
.fb-open-btn{width:100%;padding:14px 16px;border-radius:14px;background:var(--p);color:#fff;font-size:14px;font-weight:800;border:none;cursor:pointer;font-family:'Inter',sans-serif;transition:all .15s;box-shadow:0 10px 24px rgba(76,29,149,.18)}
.fb-open-btn:hover{background:var(--p-dark);transform:translateY(-1px)}
.fb-open-btn:disabled{opacity:.6;cursor:not-allowed;transform:none}
.topbar-fb{width:auto;padding:8px 14px;border-radius:7px;font-size:12px;box-shadow:none}
.admin-wrap{padding:0;display:flex;flex-direction:column;gap:16px}
.admin-search-wrap{width:min(420px,100%)}
.admin-search{padding:10px 13px}
.admin-stats{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}
.admin-stat{background:var(--surf);border:1px solid var(--card2);border-radius:10px;padding:14px 16px}
.admin-stat-k{font-size:10px;font-weight:700;letter-spacing:.7px;text-transform:uppercase;color:var(--th);margin-bottom:6px}
.admin-stat-v{font-size:24px;font-weight:800;letter-spacing:-.4px;color:var(--t)}
.admin-table{background:var(--surf);border:1px solid var(--card2);border-radius:12px;overflow:hidden}
.admin-row{display:grid;gap:12px;align-items:center;padding:12px 16px}
.admin-head{grid-template-columns:1fr;font-size:10px;font-weight:800;letter-spacing:.8px;text-transform:uppercase;color:var(--th);background:var(--card);border-bottom:1px solid var(--card2)}
.admin-summary{grid-template-columns:1fr}
.admin-group{border-top:1px solid var(--card2)}
.admin-group:first-child{border-top:none}
.admin-group summary{list-style:none;cursor:pointer}
.admin-group summary::-webkit-details-marker{display:none}
.admin-summary{background:rgba(76,29,149,.03);transition:background .15s}
.admin-summary:hover{background:rgba(76,29,149,.06)}
.admin-main{min-width:0}
.admin-owner{font-size:13px;font-weight:800;color:var(--t);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.admin-owner-inline{display:flex;align-items:center;gap:10px;min-width:0}
.admin-email{min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.admin-toggle{border:1px solid var(--card2);background:var(--card);color:var(--p);font-size:10px;font-weight:800;padding:4px 9px;border-radius:999px;cursor:pointer;flex-shrink:0;transition:all .15s;font-family:'Inter',sans-serif}
.admin-toggle:hover{background:var(--p);border-color:var(--p);color:#fff}
.admin-sub{font-size:11px;color:var(--th);margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.admin-pill{display:inline-flex;align-items:center;justify-content:center;min-width:30px;padding:4px 9px;border-radius:999px;background:var(--p2);color:var(--p-dark);font-size:11px;font-weight:800}
.admin-pill.muted{background:rgba(148,163,184,.12);color:var(--slate)}
.admin-muted{font-size:12px;font-weight:700;color:var(--t);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.admin-subrows{background:linear-gradient(180deg, rgba(248,250,252,.95), rgba(255,255,255,.88))}
.admin-subhead{grid-template-columns:2.2fr .7fr 1fr 1fr .8fr;font-size:10px;font-weight:800;letter-spacing:.8px;text-transform:uppercase;color:var(--th);background:rgba(76,29,149,.05);border-top:1px solid var(--card2);border-bottom:1px solid var(--card2)}
.admin-subrow{grid-template-columns:2.2fr .7fr 1fr 1fr .8fr;border-top:1px solid var(--card2);padding-left:28px}
.admin-subrow .admin-owner{font-size:12px}
.admin-subrow .admin-sub{font-size:10px}
.admin-group[open] .admin-summary{border-bottom:1px solid var(--card2)}
.admin-group[open] .admin-summary .admin-pill::after{content:"";display:none}
.admin-table .empty{padding:42px 24px}
.fb-modal-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.45);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:500}
.fb-modal{width:min(560px,calc(100vw - 32px));background:var(--surf);border-radius:18px;border:1px solid var(--card2);box-shadow:0 30px 80px rgba(15,23,42,.24);overflow:hidden}
.fb-modal-top{padding:16px 18px;border-bottom:1px solid var(--card2);display:flex;align-items:flex-start;justify-content:space-between;gap:12px;background:linear-gradient(180deg, rgba(76,29,149,.05), rgba(255,255,255,.9))}
.fb-modal-title{font-size:18px;font-weight:800;color:var(--t);margin-bottom:4px}
.fb-modal-sub{font-size:12px;color:var(--tm);line-height:1.5}
.fb-close{border:none;background:var(--card);color:var(--slate);border-radius:999px;width:32px;height:32px;cursor:pointer;font-size:18px;line-height:1}
.fb-modal-body{padding:16px 18px 18px}
.fb-ta{width:100%;resize:vertical;min-height:120px;background:var(--surf);border:1.5px solid var(--card2);border-radius:12px;padding:12px 14px;font-size:13px;color:var(--t);font-family:'Inter',sans-serif;outline:none;transition:all .15s;line-height:1.45}
.fb-ta:focus{border-color:var(--p);box-shadow:0 0 0 3px rgba(76,29,149,.08)}
.fb-modal-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:14px}
.fb-secondary{padding:10px 14px;border-radius:10px;background:var(--card);color:var(--t);font-size:12px;font-weight:700;border:1px solid var(--card2);cursor:pointer;font-family:'Inter',sans-serif}
.fb-primary{padding:10px 14px;border-radius:10px;background:var(--p);color:#fff;font-size:12px;font-weight:700;border:none;cursor:pointer;font-family:'Inter',sans-serif}
.fb-primary:disabled{opacity:.6;cursor:not-allowed}
.main{padding:24px 28px;overflow-y:auto}
.step-badge{font-size:11px;font-weight:600;color:var(--p-dark);background:var(--p2);border-radius:5px;padding:3px 9px;display:inline-flex;align-items:center;gap:5px;margin-bottom:12px}
.pg-title{font-size:22px;font-weight:800;letter-spacing:-.4px;color:var(--t);display:flex;align-items:center;gap:10px;margin-bottom:5px}
.pg-sub{font-size:13px;color:var(--tm)}
.val-err{display:none;align-items:center;gap:9px;background:var(--red-light);border:1px solid var(--red);border-radius:8px;padding:11px 14px;font-size:12px;color:var(--red);margin-bottom:16px}
.val-err.show{display:flex}
.gate{display:flex;align-items:center;justify-content:center;min-height:calc(100vh - 62px);padding:40px 20px}
.gate-card{background:var(--surf);border:1px solid var(--card2);border-radius:20px;padding:40px 44px;max-width:460px;width:100%;position:relative;overflow:hidden}
.gate-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--p),var(--p2))}
.gate-icon{font-size:48px;margin-bottom:20px;text-align:center}
.gate-h{font-size:24px;font-weight:800;letter-spacing:-.5px;margin-bottom:8px;text-align:center;color:var(--t)}
.gate-p{font-size:13px;color:var(--tm);line-height:1.7;margin-bottom:28px;text-align:center}
.gate-lbl{font-size:11px;font-weight:700;color:var(--th);margin-bottom:7px;letter-spacing:.8px;display:block;text-transform:uppercase}
.gate-inp-wrap{position:relative;margin-bottom:12px}
.gate-inp{width:100%;background:var(--card);border:2px solid var(--card2);border-radius:12px;padding:14px 46px 14px 16px;font-size:14px;color:var(--t);font-family:'Inter',sans-serif;outline:none;transition:all .2s;letter-spacing:.5px}
.gate-inp:focus{border-color:var(--p);box-shadow:0 0 0 4px rgba(76,29,149,.08);background:var(--surf)}
.gate-inp.err{border-color:var(--red)!important}.gate-inp.ok{border-color:var(--green)!important}
.gate-eye{position:absolute;right:14px;top:50%;transform:translateY(-50%);background:none;border:none;color:var(--th);cursor:pointer;font-size:16px}
.gate-hint{font-size:11px;color:var(--th);line-height:1.6;margin-bottom:22px;display:flex;align-items:flex-start;gap:8px;background:rgba(76,29,149,.04);padding:11px 14px;border-radius:10px;border:1px solid var(--b)}
.gate-err{display:none;background:var(--red-light);border:1px solid var(--red);border-radius:10px;padding:12px 14px;font-size:12px;color:var(--red);line-height:1.5;margin-bottom:16px}
.gate-err.show{display:block}
.gate-ok{display:none;background:rgba(34,197,94,.06);border:1px solid rgba(34,197,94,.2);border-radius:10px;padding:12px 14px;font-size:12px;color:#16a34a;margin-bottom:16px;align-items:flex-start;gap:9px}
.gate-ok.show{display:flex}
.btn-connect{width:100%;padding:14px;border-radius:12px;font-size:14px;font-weight:700;cursor:pointer;border:none;background:linear-gradient(135deg,var(--p),var(--slate));color:#fff;font-family:'Inter',sans-serif;transition:all .2s;display:flex;align-items:center;justify-content:center;gap:8px;box-shadow:0 4px 16px rgba(76,29,149,.25)}
.btn-connect:hover{transform:translateY(-1px)}.btn-connect:disabled{opacity:.4;cursor:not-allowed;transform:none}
.btn-connect.done{background:linear-gradient(135deg,var(--green),#16a34a)}
.demo-link{display:block;text-align:center;margin-top:14px;font-size:12px;color:var(--th);cursor:pointer}
.demo-link:hover{color:var(--p)}
.search-wrap{position:relative}
.search-inp{width:100%;background:var(--surf);border:1.5px solid var(--card2);border-radius:9px;padding:12px 230px 12px 42px;font-size:13px;color:var(--t);font-family:'Inter',sans-serif;outline:none;transition:all .18s;font-weight:500}
.search-inp:focus{border-color:var(--p);box-shadow:0 0 0 3px rgba(76,29,149,.06)}
.search-inp::placeholder{color:var(--th);font-weight:400}
.scope-filter{position:absolute;right:44px;top:50%;transform:translateY(-50%);display:flex;gap:4px;align-items:center;z-index:2}
.scope-filter-btn{border:1px solid var(--card2);background:var(--surf);color:var(--slate);cursor:pointer;font-size:10px;font-weight:700;padding:4px 8px;border-radius:999px;transition:all .15s;font-family:'Inter',sans-serif}
.scope-filter-btn:hover{background:var(--card);color:var(--t);border-color:var(--bh)}
.scope-filter-btn.on{background:var(--p);color:#fff;border-color:var(--p)}
.scope-filter-btn.on:hover{background:var(--p-dark);color:#fff;border-color:var(--p-dark)}
.search-ico{position:absolute;left:14px;top:50%;transform:translateY(-50%);font-size:15px;pointer-events:none}
.search-clear{position:absolute;right:12px;top:50%;transform:translateY(-50%);background:var(--card2);border:1px solid var(--card2);color:var(--slate);cursor:pointer;font-size:10px;padding:2px 7px;border-radius:5px;font-family:'Inter',sans-serif;z-index:3}
.tree-list{background:var(--surf);border:1px solid var(--bh);border-radius:10px;overflow:hidden;max-height:320px;overflow-y:auto;box-shadow:0 12px 32px rgba(76,29,149,.1)}
.tree-list::-webkit-scrollbar{width:3px}.tree-list::-webkit-scrollbar-thumb{background:var(--card2);border-radius:3px}
.tree-section{border-bottom:1px solid var(--card2)}.tree-section:last-child{border-bottom:none}
.tree-sec-hdr{padding:8px 14px 6px;font-size:10px;font-weight:700;letter-spacing:.9px;text-transform:uppercase;position:sticky;top:0;z-index:2;background:var(--card);display:flex;align-items:center;gap:7px;border-bottom:1px solid var(--card2)}
.tree-item{display:flex;align-items:center;gap:10px;padding:10px 14px;cursor:pointer;transition:background .1s}
.tree-item:hover,.tree-item.selected{background:rgba(76,29,149,.04)}
.tree-item.indent1{padding-left:28px}.tree-item.indent2{padding-left:44px}
.tree-ico{width:26px;height:26px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0}
.tree-name{font-size:13px;font-weight:500;color:var(--t);flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tree-path{font-size:10px;color:var(--th);margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tree-badge{font-size:10px;font-weight:700;padding:2px 8px;border-radius:20px;flex-shrink:0}
.tree-check{width:22px;height:22px;border-radius:7px;border:1px solid var(--card2);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;color:var(--p);flex-shrink:0}
.tree-check.on{background:var(--p);border-color:var(--p);color:#fff}
.tree-count{font-size:10px;color:var(--th);flex-shrink:0;margin-right:4px}
.tree-loading{padding:20px;text-align:center;font-size:12px;color:var(--tm);display:flex;align-items:center;justify-content:center;gap:8px}
.tree-empty{padding:20px;text-align:center;font-size:12px;color:var(--th)}
.sel-card{display:flex;align-items:center;gap:12px;background:var(--surf);border-radius:10px;padding:14px 16px;margin-bottom:16px;border:1.5px solid var(--p2);box-shadow:0 2px 8px rgba(190,242,100,.15);transition:all .15s}
.sel-card.compact{margin-bottom:8px;padding:11px 13px;box-shadow:none}
.sel-card-ico{width:38px;height:38px;border-radius:9px;background:var(--p);color:#fff;display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0}
.sel-card-name{font-size:14px;font-weight:700;color:var(--t)}.sel-card-path{font-size:11px;color:var(--th);margin-top:1px}
.sel-card-badge{margin-left:auto;font-size:10px;font-weight:700;padding:3px 9px;border-radius:5px;background:var(--p2);color:var(--p-dark);flex-shrink:0}
.sel-card-change{display:inline-flex;align-items:center;gap:4px;font-size:11px;color:#4C1D95;background:#EDE9FE;border:1px solid #DDD6FE;padding:5px 11px;border-radius:999px;cursor:pointer;margin-left:8px;white-space:nowrap;font-weight:700;letter-spacing:.2px;transition:background .18s ease,color .18s ease,border-color .18s ease,transform .18s ease,box-shadow .18s ease}
.sel-card-change:hover{background:linear-gradient(135deg,#4C1D95 0%,#7C3AED 100%);color:#FFFFFF;border-color:#4C1D95;transform:translateY(-1px);box-shadow:0 6px 14px rgba(76,29,149,.22)}
.sel-card-change:active{transform:translateY(0);box-shadow:0 2px 6px rgba(76,29,149,.18)}
.ch-hint{margin:0;padding:11px 14px;display:flex;align-items:flex-start;gap:10px;background:linear-gradient(135deg,#FFFBEB 0%,#FEF3C7 100%);border-top:1px solid #FDE68A;color:#92400E;font-size:11.5px;line-height:1.55;transition:background .2s ease}
.ch-hint:hover{background:linear-gradient(135deg,#FEF3C7 0%,#FDE68A 100%)}
.ch-hint .ch-hint-ico{flex-shrink:0;width:22px;height:22px;border-radius:50%;background:#F59E0B;color:#FFFFFF;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;box-shadow:0 2px 6px rgba(245,158,11,.35)}
.ch-hint strong{color:#78350F;font-weight:700}
.scope-remove{border:1px solid var(--card2);background:transparent;color:var(--slate);border-radius:7px;height:28px;padding:0 9px;font-size:11px;font-weight:700;cursor:pointer;font-family:'Inter',sans-serif}
.scope-remove:hover{border-color:var(--bh);color:var(--t);background:var(--card)}
.multi-scope-box{margin-bottom:12px}
.scope-hint{display:flex;align-items:flex-start;gap:10px;border-radius:8px;padding:11px 13px;font-size:12px;color:var(--tm);line-height:1.6;margin-bottom:16px;border:1px solid}
.live-badge{display:inline-flex;align-items:center;gap:7px;background:rgba(34,197,94,.06);border:1px solid rgba(34,197,94,.2);border-radius:6px;padding:5px 12px;font-size:11px;color:#16a34a;margin-bottom:14px;font-weight:600}
.live-dot{width:6px;height:6px;border-radius:50%;background:#22C55E;animation:pulse 2s infinite;flex-shrink:0}
.stats-row{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:18px}
.stat-card{background:var(--surf);border:1px solid var(--card2);border-radius:10px;padding:16px 18px;display:flex;align-items:center;gap:14px;transition:all .15s}
.stat-card:hover{border-color:var(--bh)}
.stat-card-ico{width:36px;height:36px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
.stat-card-num{font-size:26px;font-weight:800;letter-spacing:-.5px;line-height:1}
.stat-card-lbl{font-size:10px;color:var(--th);margin-top:3px;font-weight:500;text-transform:uppercase;letter-spacing:.4px}
.stat-card-num.blue{color:var(--blue)}.stat-card-num.green{color:var(--green)}.stat-card-num.red{color:var(--red)}.stat-card-num.amber{color:var(--amber)}.stat-card-num.purple{color:var(--p)}.stat-card-num.slate{color:var(--slate)}
.breakdown-card{background:var(--surf);border:1px solid var(--card2);border-radius:10px;padding:18px 20px;margin-bottom:14px}
.breakdown-title{font-size:11px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:var(--th);margin-bottom:14px;display:flex;align-items:center;gap:7px}
.brow{display:flex;align-items:center;gap:14px;margin-bottom:12px}.brow:last-child{margin-bottom:0}
.brow-label{font-size:12px;color:var(--tm);width:130px;flex-shrink:0;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.brow-bar-wrap{flex:1;height:6px;background:var(--slate-light);border-radius:10px;overflow:hidden}
.brow-bar{height:100%;border-radius:10px;transition:width .6s ease}
.brow-count{font-size:12px;color:var(--t);width:30px;text-align:right;font-weight:700;flex-shrink:0}
.prog-wrap{background:var(--surf);border:1px solid var(--card2);border-radius:10px;padding:16px 18px;margin-bottom:16px}
.prog-row{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
.prog-lbl{font-size:12px;color:var(--tm);font-weight:500}
.prog-pct{font-size:20px;font-weight:800;color:var(--p);letter-spacing:-.3px}
.prog-bar{height:8px;background:var(--slate-light);border-radius:10px;overflow:hidden}
.prog-fill{height:100%;background:var(--p);border-radius:10px;transition:width .8s ease}
.hygiene-card{background:var(--surf);border:1px solid var(--card2);border-radius:10px;padding:16px 18px;margin-bottom:14px}
.hygiene-title{font-size:11px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:var(--th);margin-bottom:12px;display:flex;align-items:center;gap:7px}
.hygiene-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.hygiene-item{display:flex;align-items:center;gap:10px;background:var(--card);border-radius:8px;padding:11px 13px}
.hygiene-ico{font-size:16px;flex-shrink:0}
.hygiene-num{font-size:20px;font-weight:800;letter-spacing:-.3px}
.hygiene-lbl{font-size:10px;color:var(--th);margin-top:2px;font-weight:500}
.hygiene-num.warn{color:var(--amber)}.hygiene-num.danger{color:var(--red)}.hygiene-num.ok{color:var(--green)}
.met-bar{display:flex;align-items:center;gap:6px;margin-bottom:14px;font-size:12px;color:var(--tm)}
.met-bar strong{color:var(--p);font-weight:700}
.mall{margin-left:auto;font-size:11px;color:var(--p);background:rgba(76,29,149,.06);border:1px solid var(--b);cursor:pointer;font-family:'Inter',sans-serif;padding:4px 11px;border-radius:6px;font-weight:600}
.mgrid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.mc{display:flex;align-items:flex-start;gap:10px;background:var(--surf);border:1.5px solid var(--card2);border-radius:9px;padding:14px 15px;min-height:64px;cursor:pointer;transition:all .15s}
.mc.on{border-color:var(--p);background:rgba(76,29,149,.03)}
.mc:hover:not(.on){border-color:var(--bh);background:var(--card)}
.mchk{width:16px;height:16px;border-radius:4px;border:1.5px solid var(--slate);display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px;font-size:9px}
.mc.on .mchk{background:var(--p);border-color:var(--p);color:#fff}
.mico{font-size:16px;flex-shrink:0;margin-top:2px}.mc-copy{min-width:0;font-size:12px;line-height:1.45;color:var(--tm)}.mn{font-size:12px;font-weight:700;color:var(--t)}.md{font-size:12px;color:var(--tm)}
.pgrid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:18px}
.pc{background:var(--surf);border:1.5px solid var(--card2);border-radius:10px;padding:15px 14px;cursor:pointer;display:flex;align-items:center;gap:12px;transition:all .15s}
.pc:hover{border-color:var(--bh);background:var(--card)}.pc.on{border-color:var(--p);background:rgba(76,29,149,.04)}
.pico{width:40px;height:40px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0}
.pn{font-size:13px;font-weight:700;color:var(--t)}.ph{font-size:10px;color:var(--tm);margin-top:1px}
.pchk{margin-left:auto;width:18px;height:18px;border-radius:50%;background:var(--p);display:flex;align-items:center;justify-content:center;font-size:9px;color:#fff;opacity:0;transition:opacity .15s}
.pc.on .pchk{opacity:1}
.flbl{font-size:11px;font-weight:600;color:var(--tm);margin-bottom:6px;letter-spacing:.3px;text-transform:uppercase}
.fwrap{position:relative;margin-bottom:18px}
.cu-sel,.cu-inp{width:100%;background:var(--surf);border:1.5px solid var(--card2);border-radius:9px;padding:10px 13px;font-size:13px;color:var(--t);font-family:'Inter',sans-serif;appearance:none;outline:none;transition:all .15s}
.cu-sel:hover,.cu-sel:focus,.cu-inp:hover,.cu-inp:focus{border-color:var(--p);background:var(--card)}
.sarr{position:absolute;right:12px;top:50%;transform:translateY(-50%);pointer-events:none;color:var(--th);font-size:11px}
.nbox{background:var(--card);border:1px solid var(--b);border-radius:9px;padding:11px 13px;font-size:11px;color:var(--tm);line-height:1.6;display:flex;gap:9px;align-items:flex-start;margin-top:13px}
.channel-wrap{position:relative;margin-bottom:8px}
.channel-dd{position:absolute;left:0;right:0;top:calc(100% + 6px);background:var(--surf);border:1px solid var(--card2);border-radius:10px;box-shadow:0 16px 30px rgba(30,27,75,.12);z-index:25;overflow:hidden;max-height:240px;overflow-y:auto}
.channel-dd-head{padding:9px 12px;font-size:10px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:var(--th);background:var(--card);border-bottom:1px solid var(--card2);display:flex;justify-content:space-between;gap:8px}
.channel-dd-item{padding:10px 12px;display:flex;align-items:center;justify-content:space-between;gap:10px;cursor:pointer;border-top:1px solid var(--card2);transition:background .12s}
.channel-dd-item:first-child{border-top:none}
.channel-dd-item:hover{background:rgba(76,29,149,.04)}
.channel-dd-section{padding:7px 12px;font-size:10px;font-weight:700;letter-spacing:.7px;text-transform:uppercase;color:var(--th);background:rgba(76,29,149,.03);border-top:1px solid var(--card2)}
.channel-dd-name{font-size:12px;font-weight:600;color:var(--t);min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.channel-dd-meta{font-size:10px;color:var(--th);flex-shrink:0}
.channel-dd-empty{padding:12px;font-size:12px;color:var(--th)}
.dgrid{display:flex;gap:7px;flex-wrap:wrap;margin-bottom:18px}
.dp{width:42px;height:42px;border-radius:9px;font-size:11px;font-weight:700;border:1.5px solid var(--card2);cursor:pointer;background:var(--surf);color:var(--slate);display:flex;align-items:center;justify-content:center;transition:all .15s}
.dp.on{background:var(--p);color:#fff;border-color:var(--p)}.dp:hover:not(.on){border-color:var(--bh);color:var(--t);background:var(--card)}
.tgrid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:18px}
input[type="time"]{color-scheme:light}
.sum-card{background:var(--surf);border:1px solid var(--card2);border-radius:10px;padding:15px 17px;margin-bottom:18px}
.sum-ttl{font-size:10px;font-weight:700;letter-spacing:.9px;text-transform:uppercase;color:var(--th);margin-bottom:12px}
.sr{display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--card2)}.sr:last-child{border-bottom:none}
.sk{font-size:12px;color:var(--tm);font-weight:500}.sv{font-size:12px;font-weight:600;color:var(--t);text-align:right}
.chip{display:inline-flex;align-items:center;gap:4px;font-size:10px;padding:3px 9px;border-radius:5px;font-weight:600}
.chip-p{background:rgba(76,29,149,.1);color:var(--p)}.chip-g{background:rgba(34,197,94,.1);color:#16a34a}.chip-a{background:rgba(217,119,6,.1);color:var(--amber)}.chip-b{background:rgba(59,130,246,.1);color:var(--blue)}
.sfoot{display:flex;align-items:center;justify-content:space-between;padding-top:18px;margin-top:12px;border-top:1px solid var(--card2)}
.bg{padding:9px 18px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;border:1.5px solid var(--slate);background:transparent;color:var(--slate);font-family:'Inter',sans-serif;transition:all .15s}
.bg:hover{border-color:var(--bh);color:var(--t);background:var(--slate-light)}
.bp{padding:9px 22px;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;border:none;background:var(--p);color:#fff;font-family:'Inter',sans-serif;transition:all .15s}
.bp:hover{background:var(--p-dark)}.bp:disabled{opacity:.5;cursor:not-allowed}
.bs{padding:9px 22px;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;border:none;background:var(--green);color:#fff;font-family:'Inter',sans-serif;transition:all .15s}
.bs:hover{opacity:.88}.bs:disabled{opacity:.5;cursor:not-allowed}
.succ{display:flex;flex-direction:column;align-items:center;text-align:center;padding:56px 28px}
.succ-ring{width:68px;height:68px;border-radius:50%;background:var(--p2);border:2px solid var(--p2-dark);display:flex;align-items:center;justify-content:center;font-size:28px;margin-bottom:16px;color:var(--p-dark)}
.succ-h{font-size:22px;font-weight:800;letter-spacing:-.4px;margin-bottom:6px;color:var(--t)}
.succ-p{font-size:13px;color:var(--tm);line-height:1.7;max-width:360px;margin-bottom:20px}
.succ-chips{display:flex;gap:7px;flex-wrap:wrap;justify-content:center;margin-bottom:24px}
.succ-btns{display:flex;gap:8px}
.lh{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:18px}
.lt{font-size:20px;font-weight:800;letter-spacing:-.4px;color:var(--t)}.ls{font-size:11px;color:var(--tm);margin-top:2px}
.saved-wrap{padding:24px 28px 36px;width:100%;max-width:none;position:relative;isolation:isolate}
.saved-day-filter{display:flex;flex-wrap:wrap;gap:6px;margin:0 0 14px}
.saved-day-chip{height:28px;padding:0 11px;border-radius:999px;border:1px solid var(--card2);background:var(--surf);color:var(--slate);font-size:11px;font-weight:700;cursor:pointer;transition:all .15s;font-family:'Inter',sans-serif}
.saved-day-chip:hover{border-color:var(--bh);background:var(--card);color:var(--t)}
.saved-day-chip.on{background:var(--p);border-color:var(--p);color:#fff}
.rcards{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr));gap:14px;align-items:start;width:100%}
.rcard{background:linear-gradient(180deg,rgba(255,255,255,.9),var(--surf));border:1px solid rgba(148,163,184,.2);border-radius:10px;overflow:visible;transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease;min-width:0;position:relative;isolation:isolate;box-shadow:0 14px 28px rgba(15,23,42,.06),0 2px 6px rgba(15,23,42,.04)}
.rcard::before{content:"";position:absolute;inset:-14px;z-index:-1;border-radius:22px;background:radial-gradient(circle at 18% 18%, rgba(96,165,250,.28), transparent 56%),radial-gradient(circle at 82% 22%, rgba(129,140,248,.18), transparent 48%),radial-gradient(circle at 50% 100%, rgba(56,189,248,.14), transparent 58%);opacity:.28;filter:blur(14px);transition:opacity .18s ease, transform .18s ease;pointer-events:none}
.rcard:hover,.rcard.is-scroll-active{border-color:rgba(96,165,250,.35);box-shadow:0 20px 38px rgba(37,99,235,.14),0 10px 22px rgba(15,23,42,.1);transform:translateY(-2px)}
.rcard:hover::before,.rcard.is-scroll-active::before{opacity:.72;transform:scale(1.01)}
.rhero{height:92px;padding:13px 14px;display:flex;align-items:flex-start;justify-content:space-between;border-bottom:1px solid var(--card2);background:linear-gradient(135deg,rgba(76,29,149,.1),rgba(78,205,196,.14))}
.rhero-ico{width:42px;height:42px;border-radius:10px;background:rgba(255,255,255,.82);border:1px solid rgba(255,255,255,.7);display:flex;align-items:center;justify-content:center;font-size:20px}
.rbody{padding:14px 15px 15px}
.rb{min-width:0}.rn{font-size:14px;font-weight:800;color:var(--t);margin-bottom:10px;line-height:1.3;word-break:break-word}
.rm{display:grid;grid-template-columns:1fr;gap:7px;margin-bottom:12px}
.rbox{border:1px solid var(--card2);border-radius:8px;padding:8px 9px;background:var(--card);min-width:0}
.rbox-k{font-size:9px;font-weight:800;letter-spacing:.6px;text-transform:uppercase;color:var(--th);margin-bottom:3px}
.rbox-v{font-size:11px;font-weight:650;color:var(--t);line-height:1.35;overflow-wrap:anywhere}
.ract{display:flex;gap:6px;align-items:center;justify-content:flex-end;border-top:1px solid var(--card2);padding-top:11px}
.sbadge{display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:600;padding:2px 7px;border-radius:5px}
.sbadge.act{background:rgba(34,197,94,.1);color:#16a34a}.sbadge.pause{background:rgba(217,119,6,.1);color:var(--amber)}
.sdot{width:5px;height:5px;border-radius:50%;background:currentColor}
.mp2{display:inline-flex;align-items:center;gap:3px;font-size:10px;color:var(--tm);font-weight:500}
.ia{height:30px;min-width:30px;padding:0 8px;border-radius:7px;border:1px solid var(--card2);background:transparent;display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--slate);font-size:12px;font-weight:700;transition:all .15s}
.ia:hover{border-color:var(--bh);color:var(--t);background:var(--card)}
.edit-panel{border-top:1px solid var(--card2);margin-top:11px;padding-top:12px}
.edit-grid{display:grid;grid-template-columns:1fr;gap:10px}
.edit-actions{display:flex;align-items:center;justify-content:flex-end;gap:7px;margin-top:12px}
.mini-days{display:flex;gap:5px;flex-wrap:wrap}
.mini-day{width:34px;height:32px;border-radius:8px;font-size:10px;font-weight:800;border:1.5px solid var(--card2);cursor:pointer;background:var(--surf);color:var(--slate);display:flex;align-items:center;justify-content:center}
.mini-day.on{background:var(--p);color:#fff;border-color:var(--p)}
.edit-err{font-size:11px;color:var(--red);margin-top:8px;line-height:1.35}
.empty{text-align:center;padding:60px 24px;color:var(--tm);font-size:13px}
.empty-icon{font-size:36px;margin-bottom:12px}
.sub-items-box{background:var(--surf);border:1px solid var(--card2);border-radius:10px;overflow:hidden;margin-bottom:16px;max-height:400px;overflow-y:auto}
.sub-item-row{display:flex;align-items:center;gap:11px;padding:11px 14px;cursor:pointer;transition:background .12s,border-color .12s;border-top:1px solid var(--card2)}
.sub-item-row:first-child{border-top:none}
.sub-item-row:hover{background:rgba(76,29,149,.03)}
.sub-item-row.selected{background:rgba(76,29,149,.06);border-color:rgba(76,29,149,.15)}
.sub-item-ico{width:26px;height:26px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0}
.sub-item-name{font-size:13px;font-weight:500;color:var(--t);flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.sub-item-meta{font-size:10px;color:var(--th);margin-top:1px}
.sub-item-badge{font-size:10px;font-weight:700;padding:2px 8px;border-radius:5px;flex-shrink:0}
.sub-item-check{width:20px;height:20px;border:2px solid var(--card2);border-radius:6px;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:12px;color:#FFFFFF;background:var(--surf);transition:all .15s ease}
.sub-item-row:hover .sub-item-check{border-color:rgba(76,29,149,.3)}
.sub-item-row.selected .sub-item-check{background:#4C1D95;border-color:#4C1D95}
.space-drill-hint{font-size:11px;color:var(--th);margin-bottom:8px;padding:0 2px;font-style:italic}
.demo-banner{background:rgba(217,119,6,.07);border:1px solid rgba(217,119,6,.2);border-radius:8px;padding:9px 13px;font-size:11px;color:var(--amber);margin-bottom:14px;display:flex;align-items:center;gap:8px}
@media (max-width:720px){
  .saved-wrap{padding:18px 12px 28px}
  .rcards{grid-template-columns:minmax(0,1fr)}
  .lh{align-items:stretch}
  .lh .new-btn{align-self:flex-start}
}
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = CSS;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
//  CONNECTION GATE — JWT auth flow
// ─────────────────────────────────────────────────────────────────────────────
function ConnectionGate({ onConnected }) {
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [okDetail, setOkDetail] = useState("");
  const googleClientRef = useRef(null);

  useEffect(() => {
    let alive = true;
    loadGoogleScript()
      .then(() => {
        if (!alive || !window.google?.accounts?.oauth2) return;
        googleClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: "openid email profile",
          prompt: "select_account",
          callback: async (response) => {
            if (!alive) return;
            if (response.error) {
              setErrMsg(response.error_description || response.error || "Google sign-in failed");
              setLoading(false);
              return;
            }
            await connectWithGoogle(response.access_token);
          },
        });
      })
      .catch(() => {
        if (!alive) return;
        setErrMsg("Google sign-in is unavailable right now.");
      });

    return () => {
      alive = false;
    };
  }, []);

  async function connectWithGoogle(accessToken) {
    setErrMsg("");
    setLoading(true);
    try {
      const authRes = await fetch(`${SERVER}/api/auth/google/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken }),
      });
      const authData = await authRes.json();
      if (!authData.success) {
        setErrMsg(authData.error || "Google sign-in failed");
        setLoading(false);
        return;
      }

      localStorage.setItem(JWT_KEY, authData.jwtToken);
      sessionStorage.removeItem(CLICKUP_KEY);

      const ws = authData.workspaces?.[0];
      setOkMsg(`Signed in as ${authData.email || "user"}`);
      setOkDetail("Loading spaces, folders and lists…");

      let tree = [];
      if (ws?.id) {
        const treeRes = await fetch(`${SERVER}/api/workspace?teamId=${ws.id}`, {
          headers: { Authorization: `Bearer ${authData.jwtToken}` },
        });
        const treeData = await treeRes.json();
        tree = treeData.tree || [];
      }

      onConnected({
        jwtToken: authData.jwtToken,
        clickupToken: null,
        wsName: authData.wsName,
        workspaces: authData.workspaces || [],
        tree,
        email: authData.email || "",
        userName: authData.userName || "",
        isAdmin: !!authData.isAdmin,
        isDemo: false,
      });
    } catch {
      setErrMsg(`Cannot reach server at ${SERVER}. Make sure it is running.`);
    } finally {
      setLoading(false);
    }
  }

  function startGoogleSignIn() {
    setErrMsg("");
    setOkMsg("");
    if (!googleClientRef.current) {
      setErrMsg("Google sign-in is still loading. Try again in a moment.");
      return;
    }
    setLoading(true);
    googleClientRef.current.requestAccessToken({ prompt: "select_account" });
  }

  return (
    <div className="gate">
      <div className="gate-card anim">
        <div className="gate-icon">🔐</div>
        <div className="gate-h">Sign in to Pragati-AI</div>
        <p className="gate-p">Use your Sigma or LendFoundry Google account to continue directly to your ClickUp reports.</p>
        {errMsg && <div className="gate-err show">✗ {errMsg}</div>}
        {okMsg  && <div className="gate-ok show"><span style={{fontSize:18,flexShrink:0}}>✓</span><div><strong>{okMsg}</strong><br/><span style={{color:"var(--tm)",fontSize:11}}>{okDetail}</span></div></div>}

        <button className={`btn-connect ${okMsg?"done":""}`} onClick={startGoogleSignIn} disabled={loading||!!okMsg}>
          {loading?<><span className="spinner wh sm"/><span>Signing in…</span></>:okMsg?"✓ Signed in — opening…":"Continue with Google"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  SCOPE STEP
// ─────────────────────────────────────────────────────────────────────────────
function ScopeStep({ state, setState, conn }) {
  const [search,   setSearch]   = useState("");
  const [open,     setOpen]     = useState(false);
  const [scopeFilter, setScopeFilter] = useState("All");
  const [taskData, setTaskData] = useState(null);
  const [taskLoad, setTaskLoad] = useState(false);
  const [subItems, setSubItems] = useState([]);
  const [scopeErr, setScopeErr] = useState("");
  const inputRef = useRef(null);
  const dropRef  = useRef(null);
  const tree     = conn?.tree || [];
  const isDemo   = !!conn?.isDemo;
  const selectedScopes = reportScopes(state);
  const isListMulti = selectedScopes.length > 0 && selectedScopes.every(i => i.type === "List");
  const isFolderMulti  = selectedScopes.length > 0 && selectedScopes.every(i => i.type === "Folder");
  const isMulti        = isListMulti || isFolderMulti;
  const selectedIds    = new Set(selectedScopes.map(i => String(i.id)));

  useEffect(() => {
    function h(e) { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = tree.filter(i => {
    if (scopeFilter !== "All" && i.type !== scopeFilter) return false;
    return !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.path.toLowerCase().includes(search.toLowerCase());
  });
  const grouped  = { Space:[], Folder:[], List:[] };
  filtered.forEach(i => { if (grouped[i.type]) grouped[i.type].push(i); });
  const indent     = { Space:"", Folder:"indent1", List:"indent2" };
  const hasResults = Object.values(grouped).some(g => g.length > 0);
  const filterTotal = scopeFilter === "All"
    ? tree.length
    : tree.filter(i => i.type === scopeFilter).length;

  useEffect(() => {
    if (!state.scopeItem) { setSubItems([]); return; }
    
      // If it's a Space, show direct-child Folders + ALL lists within it (recursively)
    if (state.scopeItem.type === "Space") {
      const directFolders = tree.filter(i => i.type === "Folder" && String(i.parent) === String(state.scopeItem.id));
      const allListsInSpace = tree.filter(i => {
        if (i.type !== "List") return false;
        const space = itemSpaceId(i, tree);
        return space === String(state.scopeItem.id);
      });
      setSubItems([...directFolders, ...allListsInSpace]);
    } else {
      // For Folder, show direct children only
      setSubItems(tree.filter(i => i.parent === state.scopeItem.id));
    }
  }, [state.scopeItem?.id, tree]);

  useEffect(() => {
    if (!state.scopeItem) { setTaskData(null); return; }
    // Don't fetch task data while user is in multi-selection mode
    if (isMulti) { setTaskData(null); return; }
    if (isDemo) { setTaskData(DEMO_DATA); return; }
    setTaskLoad(true); setTaskData(null);
    const wsId = conn?.workspaces?.[0]?.id;
    fetchTaskData(state.scopeItem.type, state.scopeItem.id, wsId, conn.clickupToken, true)
      .then(d => { if (!d.error) setTaskData(d); })
      .finally(() => setTaskLoad(false));
  }, [state.scopeItem?.id, isMulti]);

  function setMultiSelection(items, type) {
    // Do NOT touch scopeItem — keep the parent Space/Folder in scopeItem so the
    // drill-down view stays visible while user toggles items in scopeItems.
    setState(s => ({...s, scopeItems: items, level: items.length ? type : null}));
  }

  function toggleProject(item, parentSpace = null) {
    setScopeErr("");
    const current = reportScopes(state).filter(i => i.type === "List");
    const exists = current.some(i => String(i.id) === String(item.id));

    if (exists) {
      const remaining = current.filter(i => String(i.id) !== String(item.id));
      if (remaining.length === 0 && parentSpace) {
        setState(s => ({...s, scopeItems: [parentSpace], level: "Space"}));
      } else {
        setMultiSelection(remaining, "List");
      }
      return;
    }

    // If user has folders selected, switch mode
    const hasFolders = reportScopes(state).some(i => i.type === "Folder" && String(i.id) !== String(parentSpace?.id));
    if (hasFolders) {
      setScopeErr("You can select either Folders or Lists, not both.");
      return;
    }

    if (current.length >= 3) {
      setScopeErr("You can select maximum 3 lists at once.");
      return;
    }

    const currentSpace = current.length ? itemSpaceId(current[0], tree) : (parentSpace ? itemSpaceId(parentSpace, tree) : "");
    const nextSpace = itemSpaceId(item, tree);
    if (currentSpace && nextSpace && currentSpace !== nextSpace) {
      setScopeErr("Multiple lists must be from the same Space.");
      return;
    }

    setMultiSelection([...current, item], "List");
    setSearch("");
    if (current.length + 1 < 3) setOpen(false);
  }

  function toggleFolder(item, parentSpace = null) {
    setScopeErr("");
    const current = reportScopes(state).filter(i => i.type === "Folder" && (!parentSpace || String(i.id) !== String(parentSpace.id)));
    const exists = current.some(i => String(i.id) === String(item.id));

    if (exists) {
      const remaining = current.filter(i => String(i.id) !== String(item.id));
      if (remaining.length === 0 && parentSpace) {
        setState(s => ({...s, scopeItems: [parentSpace], level: "Space"}));
      } else {
        setMultiSelection(remaining, "Folder");
      }
      return;
    }

    // If user has lists selected, prevent mixing
    const hasProjects = reportScopes(state).some(i => i.type === "List");
    if (hasProjects) {
      setScopeErr("You can select either Folders or Lists, not both.");
      return;
    }

    if (current.length >= 4) {
      setScopeErr("You can select maximum 4 folders at once.");
      return;
    }

    const currentSpace = current.length ? itemSpaceId(current[0], tree) : (parentSpace ? itemSpaceId(parentSpace, tree) : "");
    const nextSpace = itemSpaceId(item, tree);
    if (currentSpace && nextSpace && currentSpace !== nextSpace) {
      setScopeErr("Multiple folders must be from the same Space.");
      return;
    }

    setMultiSelection([...current, item], "Folder");
    setSearch("");
    if (current.length + 1 < 4) setOpen(false);
  }

  function selectItem(item, fromSubItems = false, parentSpace = null) {
    setScopeErr("");
    
    // From within a Space drill-down: folders and lists are multi-select
    if (fromSubItems && parentSpace) {
      if (item.type === "List")   { toggleProject(item, parentSpace); return; }
      if (item.type === "Folder") { toggleFolder(item,  parentSpace); return; }
    }
    
    // From main search: list = toggle multi-select
    if (item.type === "List") { 
      toggleProject(item); 
      return; 
    }
    
    // From main search: Space or single Folder = drill down
    setState(s => ({...s, scopeItem:item, scopeItems:[item], level:item.type}));
    setSearch("");
    setOpen(false);
  }

  function clearSelection()  {
    setState(s => ({...s, scopeItem:null, scopeItems:[], level:null}));
    setSubItems([]);
    setScopeErr("");
    setSearch("");
    setScopeFilter("All");
    setOpen(true);
    setTimeout(()=>inputRef.current?.focus(),50);
  }

  return (
    <div className="anim">
      <div className="step-badge">🔍 Step 1 of 5 — Scope</div>
      <div className="pg-title"><span>🔍</span> Search in your workspace</div>
      <div className="pg-sub" style={{marginBottom:18}}>Select one Space, up to 4 Folders, or up to 3 Lists from the same Space. Each selected item becomes a separate report.</div>
      {isDemo && <div className="demo-banner">🎭 Demo mode — showing sample workspace data</div>}
      {selectedScopes.length > 0 && (
        <div className="multi-scope-box">
          {selectedScopes.map(item => {
            const m=TYPE_META[item.type];
            return (
              <div key={item.id} className="sel-card compact" style={{borderColor:m.color+"44"}}>
                <div className="sel-card-ico" style={{background:m.bg}}>{m.icon}</div>
                <div style={{flex:1,minWidth:0}}><div className="sel-card-name">{item.name}</div><div className="sel-card-path">{item.path}</div></div>
                <span className="sel-card-badge" style={{background:m.bg,color:m.color}}>{item.type==="List"?"List":item.type}</span>
                {isMulti
                  ? <button type="button" className="scope-remove" onClick={()=>item.type==="Folder"?toggleFolder(item):toggleProject(item)}>Remove</button>
                  : <span className="sel-card-change" onClick={clearSelection}>Change ›</span>}
              </div>
            );
          })}
          <div className="scope-hint" style={{background:selectedScopes.length===1?TYPE_META[selectedScopes[0].type].bg:"rgba(78,205,196,.08)",borderColor:selectedScopes.length===1?TYPE_META[selectedScopes[0].type].color+"33":"rgba(78,205,196,.25)"}}>
            <span style={{fontSize:16,flexShrink:0}}>{selectedScopes.length===1?TYPE_META[selectedScopes[0].type].icon:(isFolderMulti?"📁":"📋")}</span>
            <span>{selectedScopes.length > 1 ? `${selectedScopes.length} ${isFolderMulti?"folder":"list"} reports will be saved and sent as separate messages.` : TYPE_META[selectedScopes[0].type].label}</span>
            <span className="sel-card-change" onClick={clearSelection}>Change ›</span>
          </div>
        </div>
      )}
      {scopeErr&&<div className="val-err show">✗  {scopeErr}</div>}
      {(!state.scopeItem || (state.scopeItem.type !== "Space" && state.scopeItem.type !== "Folder" && (isListMulti || isFolderMulti))) ? (
        <div ref={dropRef} style={{position:"relative",marginBottom:16}}>
          <div className="search-wrap">
            <span className="search-ico">🔍</span>
            <input ref={inputRef} className="search-inp" placeholder={isFolderMulti?"Add another folder from this Space…":isListMulti?"Add another list from this Space…":"Search spaces, folders, lists…"} value={search} onChange={e=>{setSearch(e.target.value);setOpen(true);setScopeErr("");}} onFocus={()=>setOpen(true)} autoComplete="off" autoFocus/>
            <div className="scope-filter">
              {["All","Space","Folder","List"].map(type => (
                <button
                  key={type}
                  type="button"
                  className={`scope-filter-btn ${scopeFilter===type?"on":""}`}
                  onClick={() => {
                    setScopeFilter(type);
                    setOpen(true);
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
            {search ? <button className="search-clear" onClick={()=>{setSearch("");inputRef.current?.focus();}}>✕</button> : null}
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",padding:"6px 4px 0",fontSize:10,color:"var(--th)",fontWeight:600}}>
            <span>{scopeFilter === "All" ? `${filterTotal} items` : `${filterTotal} ${scopeFilter}${filterTotal === 1 ? "" : "s"}`}</span>
          </div>
          {open && (
            <div className="tree-list" style={{position:"absolute",top:"calc(100% + 6px)",left:0,right:0,zIndex:50}}>
              {tree.length===0 ? <div className="tree-loading"><div className="spinner"/><span>Loading…</span></div>
               : !hasResults&&search ? <div className="tree-empty">No results for "<strong>{search}</strong>"</div>
               : <>
                {(scopeFilter==="All"?["Space","Folder","List"]:[scopeFilter]).map(type => {
                  const items = grouped[type]; if (!items?.length) return null;
                  const m = TYPE_META[type];
                  return (
                    <div key={type} className="tree-section">
                      <div className="tree-sec-hdr" style={{color:m.color}}>{m.icon}&nbsp;{type==="List"?"Lists":type+"s"}<span style={{marginLeft:"auto",fontWeight:500,color:"var(--th)",fontSize:10}}>{tree.filter(i => i.type === type).length}</span></div>
                      {items.map(item => (
                        <div key={item.id} className={`tree-item ${indent[item.type]} ${selectedIds.has(String(item.id))?"selected":""}`} onMouseDown={e=>{e.preventDefault();selectItem(item);}}>
                          <div className="tree-ico" style={{background:m.bg}}>{m.icon}</div>
                          <div style={{flex:1,minWidth:0}}><div className="tree-name">{item.name}</div><div className="tree-path">{item.path}</div></div>
                          {item.taskCount!=null&&<span className="tree-count">{item.taskCount}</span>}
                          {item.type==="List"&&<span className={`tree-check ${selectedIds.has(String(item.id))?"on":""}`}>{selectedIds.has(String(item.id))?"✓":"+"}</span>}
                          <span className="tree-badge" style={{background:m.bg,color:m.color}}>{type==="List"?"List":type}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {!search && <div style={{padding:"9px 14px",borderTop:"1px solid var(--b)",fontSize:10,color:"var(--th)",display:"flex",gap:16,fontWeight:600}}><span>🌐 {tree.filter(i=>i.type==="Space").length} Spaces</span><span>📁 {tree.filter(i=>i.type==="Folder").length} Folders</span><span>📋 {tree.filter(i=>i.type==="List").length} Lists</span></div>}
               </>}
            </div>
          )}
        </div>
      ) : (
        <>
          {(state.scopeItem.type==="Folder"||state.scopeItem.type==="Space")&&subItems.length>0&&(
            <div style={{marginBottom:16}}>
              {state.scopeItem.type === "Space" ? (() => {
                const folderItems  = subItems.filter(i => i.type === "Folder");
                const listItems = subItems.filter(i => i.type === "List");
                const folderCount  = selectedScopes.filter(i => i.type === "Folder").length;
                const listCount = selectedScopes.filter(i => i.type === "List").length;
                return (
                  <>
                    <div className="space-drill-hint" style={{marginBottom:10,fontStyle:"normal",color:"var(--tm)"}}>
                      Inside <strong>{state.scopeItem.name}</strong> — pick up to <strong>4 Folders</strong> or up to <strong>3 Lists</strong> (same Space), or continue with the whole Space. Each pick becomes a separate report.
                    </div>
                    {folderItems.length > 0 && (
                      <>
                        <div style={{fontSize:11,fontWeight:700,color:"var(--tm)",margin:"4px 0 6px",textTransform:"uppercase",letterSpacing:".5px",display:"flex",alignItems:"center",gap:8}}>
                          <span>📁 Folders in this Space</span>
                          <span style={{marginLeft:"auto",color:"var(--th)",fontWeight:600}}>{folderCount}/4 selected</span>
                        </div>
                        <div className="sub-items-box" style={{marginBottom:12}}>{folderItems.map(item=>{
                          const itemData = {id:item.id,name:item.name,type:item.type,parent:state.scopeItem.id,path:item.path,taskCount:item.taskCount||0};
                          const isSelected = selectedIds.has(String(item.id));
                          return (
                            <div key={item.id} className={`sub-item-row ${isSelected?"selected":""}`} onClick={()=>selectItem(itemData, true, state.scopeItem)}>
                              <div className="sub-item-check">{isSelected?"✓":""}</div>
                              <div className="sub-item-ico" style={{background:"rgba(91,142,255,.1)"}}>📁</div>
                              <div style={{flex:1,minWidth:0}}>
                                <div className="sub-item-name">{item.name}</div>
                                <div className="sub-item-meta">Folder</div>
                              </div>
                              <span className="sub-item-badge" style={{background:"rgba(91,142,255,.1)",color:"#5B8EFF"}}>Folder</span>
                            </div>
                          );
                        })}</div>
                      </>
                    )}
                    {listItems.length > 0 && (
                      <>
                        <div style={{fontSize:11,fontWeight:700,color:"var(--tm)",margin:"4px 0 6px",textTransform:"uppercase",letterSpacing:".5px",display:"flex",alignItems:"center",gap:8}}>
                          <span>📋 All Lists in this Space</span>
                          <span style={{marginLeft:"auto",color:"var(--th)",fontWeight:600}}>{listCount}/3 selected</span>
                        </div>
                        <div className="sub-items-box">{listItems.map(item=>{
                          const itemData = {id:item.id,name:item.name,type:item.type,parent:state.scopeItem.id,path:item.path,taskCount:item.taskCount||0};
                          const isSelected = selectedIds.has(String(item.id));
                          return (
                            <div key={item.id} className={`sub-item-row ${isSelected?"selected":""}`} onClick={()=>selectItem(itemData, true, state.scopeItem)}>
                              <div className="sub-item-check">{isSelected?"✓":""}</div>
                              <div className="sub-item-ico" style={{background:"rgba(78,205,196,.1)"}}>📋</div>
                              <div style={{flex:1,minWidth:0}}>
                                <div className="sub-item-name">{item.name}</div>
                                <div className="sub-item-meta">{item.path}{item.taskCount!=null?` · ${item.taskCount} tasks`:""}</div>
                              </div>
                              <span className="sub-item-badge" style={{background:"rgba(78,205,196,.1)",color:"#4ECDC4"}}>List</span>
                            </div>
                          );
                        })}</div>
                      </>
                    )}
                  </>
                );
              })() : (
                <>
                  <div style={{fontSize:11,fontWeight:700,color:"var(--tm)",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>Inside this Folder</div>
                  <div className="space-drill-hint">Select a folder or list below, or continue with the whole Folder.</div>
                  <div className="sub-items-box">{subItems.map(item=>{
                    const itemData = {id:item.id,name:item.name,type:item.type,parent:state.scopeItem.id,path:item.path,taskCount:item.taskCount||0};
                    const isList = item.type === "List";
                    const isSelected = isList && selectedIds.has(String(item.id));
                    return (
                      <div key={item.id} className={`sub-item-row ${isSelected?"selected":""}`} onClick={()=>selectItem(itemData, true, state.scopeItem)}>
                        {isList && <div className="sub-item-check">{isSelected?"✓":""}</div>}
                        <div className="sub-item-ico" style={{background:item.type==="Folder"?"rgba(91,142,255,.1)":"rgba(78,205,196,.1)"}}>{item.type==="Folder"?"📁":"📋"}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div className="sub-item-name">{item.name}</div>
                          <div className="sub-item-meta">{item.type}{item.taskCount!=null?` · ${item.taskCount} tasks`:""}</div>
                        </div>
                        <span className="sub-item-badge" style={{background:item.type==="Folder"?"rgba(91,142,255,.1)":"rgba(78,205,196,.1)",color:item.type==="Folder"?"#5B8EFF":"#4ECDC4"}}>{item.type==="Folder"?"Folder":"List"}</span>
                        {!isList && <span style={{fontSize:11,color:"var(--th)"}}>›</span>}
                      </div>
                    );
                  })}</div>
                </>
              )}
            </div>
          )}
          {taskLoad ? <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"var(--tm)",padding:"12px 0"}}><div className="spinner sm"/>Loading task data…</div>
           : taskData ? (
            <div className="anim">
              <div className="live-badge"><span className="live-dot"/>{isDemo?"Demo — ":"Live — "}{taskData.taskCount} tasks in {state.scopeItem.name}</div>
              <div className="stats-row">
                <div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(91,142,255,.1)"}}>📊</div><div><div className="stat-card-num blue">{taskData.metrics.total}</div><div className="stat-card-lbl">Total Tasks</div></div></div>
                <div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(78,205,196,.1)"}}>✅</div><div><div className="stat-card-num green">{taskData.metrics.done}</div><div className="stat-card-lbl">Complete</div></div></div>
                <div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(255,107,107,.1)"}}>🔴</div><div><div className="stat-card-num red">{taskData.metrics.overdue}</div><div className="stat-card-lbl">Overdue</div></div></div>
            </div>
              <div className="prog-wrap"><div className="prog-row"><span className="prog-lbl">Completion rate</span><span className="prog-pct">{taskData.metrics.completion}%</span></div><div className="prog-bar"><div className="prog-fill" style={{width:`${taskData.metrics.completion}%`}}/></div></div>
              {taskData.statuses?.length>0&&<div className="breakdown-card"><div className="breakdown-title">📈 Status Breakdown</div>{taskData.statuses.slice(0,6).map((s,i)=><div key={i} className="brow"><div className="brow-label">{s.name}</div><div className="brow-bar-wrap"><div className="brow-bar" style={{width:`${Math.round(s.count/taskData.statuses[0].count*100)}%`,background:BAR_COLORS[i%6]}}/></div><div className="brow-count">{s.count}</div></div>)}</div>}
            </div>
           ) : null}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  METRICS STEP
// ─────────────────────────────────────────────────────────────────────────────
function MetricsStep({ state, setState }) {
  const allOn = state.metrics.length === METRICS.length;
  return (
    <div className="anim">
      <div className="step-badge">📊 Step 2 of 5 — Metrics</div>
      <div className="pg-title"><span>📊</span> Choose your metrics</div>
      <div className="pg-sub" style={{marginBottom:18}}>Select what data appears in your report. At least one required.</div>
      <div className="met-bar"><strong>{state.metrics.length}</strong> of {METRICS.length} selected<button className="mall" onClick={()=>setState(s=>({...s,metrics:allOn?[]:METRICS.map(m=>m.id)}))}>{allOn?"Clear all":"Select all"}</button></div>
      <div className="mgrid">{METRICS.map(m=>{const on=state.metrics.includes(m.id);return(<div key={m.id} className={`mc ${on?"on":""}`} onClick={()=>setState(s=>({...s,metrics:on?s.metrics.filter(x=>x!==m.id):[...s.metrics,m.id]}))}>
        <div className="mchk">{on?"✓":""}</div><div className="mico">{m.icon}</div><div className="mc-copy" title={`${m.name} - ${m.desc}`}><span className="mn">{m.name}</span><span className="md">{` - ${m.desc}`}</span></div>
      </div>);})}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PREVIEW STEP — Demo mode never crashes, uses DEMO_DATA
// ─────────────────────────────────────────────────────────────────────────────
function PreviewStep({ state, conn }) {
  const scopes = reportScopes(state);
  // Use the first scope as the preview anchor; falls back to state.scopeItem only if nothing else available.
  const si     = scopes[0] || state.scopeItem;
  const m      = si ? TYPE_META[si.type] : null;
  const isDemo = !!conn?.isDemo;
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!si) { setLoading(false); return; }
    if (isDemo) { setData(DEMO_DATA); setLoading(false); return; }
    setLoading(true);
    const wsId = conn?.workspaces?.[0]?.id;
    fetchTaskData(si.type, si.id, wsId, conn.clickupToken, true)
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [si?.id]);

  const inc = id => state.metrics.includes(id);

  return (
    <div className="anim">
      <div className="step-badge">👁 Step 3 of 5 — Preview</div>
      <div className="pg-title"><span>👁</span> Report preview</div>
      <div className="pg-sub" style={{marginBottom:18}}>{scopes.length>1 ? "Each selected list will be sent as a separate message." : "This is exactly what will be sent to your channel."}</div>
      {isDemo && <div className="demo-banner">🎭 Demo mode — showing sample data</div>}
      {scopes.length>1 ? (
        <div className="multi-scope-box">{scopes.map(item => {
          const meta=TYPE_META[item.type];
          return <div key={item.id} className="sel-card compact" style={{borderColor:meta.color+"44"}}><div className="sel-card-ico" style={{background:meta.bg}}>{meta.icon}</div><div style={{flex:1,minWidth:0}}><div className="sel-card-name">{item.name}</div><div className="sel-card-path">{item.path}</div></div><span className="sel-card-badge" style={{background:meta.bg,color:meta.color}}>List</span></div>;
        })}</div>
      ) : si && <div className="sel-card" style={{borderColor:m.color+"44",marginBottom:16}}><div className="sel-card-ico" style={{background:m.bg}}>{m.icon}</div><div style={{flex:1}}><div className="sel-card-name">{si.name}</div><div className="sel-card-path">{si.path}</div></div><span className="sel-card-badge" style={{background:m.bg,color:m.color}}>{si.type==="List"?"List":si.type}</span></div>}
      {loading ? <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"var(--tm)",padding:"20px 0"}}><div className="spinner sm"/>Loading report data…</div>
       : error  ? <div className="val-err show">✗  {error}</div>
       : data   ? (
        <div className="anim">
          {(inc("withoutdue")||inc("withoutassignee")||inc("blocked")||inc("onhold"))&&(
            <div className="hygiene-card"><div className="hygiene-title">🧹 Project Hygiene</div><div className="hygiene-grid">
              {inc("withoutdue")      &&<div className="hygiene-item"><div className="hygiene-ico">📅</div><div><div className={`hygiene-num ${data.metrics.withoutDueDate>0?"warn":"ok"}`}>{data.metrics.withoutDueDate}</div><div className="hygiene-lbl">Without due date</div></div></div>}
              {inc("withoutassignee") &&<div className="hygiene-item"><div className="hygiene-ico">👤</div><div><div className={`hygiene-num ${data.metrics.withoutAssignee>0?"warn":"ok"}`}>{data.metrics.withoutAssignee}</div><div className="hygiene-lbl">Without assignee</div></div></div>}
              {inc("blocked")         &&<div className="hygiene-item"><div className="hygiene-ico">🚫</div><div><div className={`hygiene-num ${data.metrics.blocked>0?"danger":"ok"}`}>{data.metrics.blocked}</div><div className="hygiene-lbl">Blocked</div></div></div>}
              {inc("onhold")          &&<div className="hygiene-item"><div className="hygiene-ico">⏸</div><div><div className={`hygiene-num ${data.metrics.onhold>0?"warn":"ok"}`}>{data.metrics.onhold}</div><div className="hygiene-lbl">On hold</div></div></div>}
            </div></div>
          )}
          {(inc("total")||inc("done")||inc("inprogress")||inc("todo")||inc("overdue")||inc("completion"))&&(
            <div className="stats-row">
              {inc("total")      &&<div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(91,142,255,.1)"}}>📊</div><div><div className="stat-card-num blue">{data.metrics.total}</div><div className="stat-card-lbl">Total</div></div></div>}
              {inc("done")       &&<div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(78,205,196,.1)"}}>✅</div><div><div className="stat-card-num green">{data.metrics.done}</div><div className="stat-card-lbl">Complete</div></div></div>}
              {inc("inprogress") &&<div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(255,209,102,.1)"}}>⚡</div><div><div className="stat-card-num amber">{data.metrics.inprogress}</div><div className="stat-card-lbl">In Progress</div></div></div>}
              {inc("todo")       &&<div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(91,142,255,.08)"}}>🔲</div><div><div className="stat-card-num blue">{data.metrics.todo}</div><div className="stat-card-lbl">To Do</div></div></div>}
              {inc("overdue")    &&<div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(255,107,107,.1)"}}>🔴</div><div><div className="stat-card-num red">{data.metrics.overdue}</div><div className="stat-card-lbl">Overdue</div></div></div>}
              {inc("completion") &&<div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(78,205,196,.1)"}}>🏁</div><div><div className="stat-card-num green">{data.metrics.completion}%</div><div className="stat-card-lbl">Completion</div></div></div>}
            </div>
          )}
          {inc("completion")&&<div className="prog-wrap"><div className="prog-row"><span className="prog-lbl">Completion rate</span><span className="prog-pct">{data.metrics.completion}%</span></div><div className="prog-bar"><div className="prog-fill" style={{width:`${data.metrics.completion}%`}}/></div></div>}
          {(inc("estimated")||inc("tracked")||inc("remaining"))&&(
            <div className="stats-row">
              {inc("estimated")&&<div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(91,142,255,.08)"}}>📐</div><div><div className="stat-card-num blue">{formatMinutes(data.metrics.estimatedTime)}</div><div className="stat-card-lbl">Estimated</div></div></div>}
              {inc("tracked")  &&<div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(78,205,196,.08)"}}>⏱</div><div><div className="stat-card-num green">{formatMinutes(data.metrics.trackedTime)}</div><div className="stat-card-lbl">Tracked</div></div></div>}
              {inc("remaining")&&<div className="stat-card"><div className="stat-card-ico" style={{background:"rgba(167,139,250,.08)"}}>⏳</div><div><div className={`stat-card-num ${data.metrics.remainingTime<0?"red":"purple"}`}>{formatMinutes(data.metrics.remainingTime)}</div><div className="stat-card-lbl">Remaining</div></div></div>}
            </div>
          )}
          {inc("status")  &&data.statuses?.length >0&&<div className="breakdown-card"><div className="breakdown-title">📈 Status Breakdown</div>{data.statuses.slice(0,6).map((s,i)=><div key={i} className="brow"><div className="brow-label">{s.name}</div><div className="brow-bar-wrap"><div className="brow-bar" style={{width:`${Math.round(s.count/data.statuses[0].count*100)}%`,background:BAR_COLORS[i%6]}}/></div><div className="brow-count">{s.count}</div></div>)}</div>}
          {inc("assignee")&&data.assignees?.length>0&&<div className="breakdown-card"><div className="breakdown-title">👥 Assignee Breakdown</div>{data.assignees.slice(0,8).map((a,i)=><div key={i} className="brow"><div className="brow-label">{a.name}</div><div className="brow-bar-wrap"><div className="brow-bar" style={{width:`${Math.round(a.count/data.assignees[0].count*100)}%`,background:BAR_COLORS[i%6]}}/></div><div className="brow-count">{a.count}</div></div>)}</div>}
          {inc("priority")&&data.priorities?.length>0&&<div className="breakdown-card"><div className="breakdown-title">🎯 Priority Breakdown</div>{data.priorities.filter(p=>p.count>0).map((p,i)=><div key={i} className="brow"><div className="brow-label">{p.priority}</div><div className="brow-bar-wrap"><div className="brow-bar" style={{width:`${Math.round(p.count/data.metrics.total*100)}%`,background:BAR_COLORS[i%6]}}/></div><div className="brow-count">{p.count}</div></div>)}</div>}
        </div>
       ) : null}
    </div>
  );
}
// Channel step 
// ─────────────────────────────────────────────────────────────────────────────
//  CHANNEL STEP — real text input, no fake hardcoded list
// ─────────────────────────────────────────────────────────────────────────────
function ChannelStep({ state, setState }) {
  const isSlack = state.platform === "slack";
  const isTeams = state.platform === "teams";
  const [slackChannels, setSlackChannels] = useState([]);
  const [teamsChannels, setTeamsChannels] = useState([]);
  const [teamsGroups, setTeamsGroups] = useState([]);
  const [slackLoaded, setSlackLoaded] = useState(false);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [slackLoading, setSlackLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsGroupLoading, setTeamsGroupLoading] = useState(false);
  const [slackError, setSlackError] = useState("");
  const [teamsError, setTeamsError] = useState("");
  const [teamsNotice, setTeamsNotice] = useState("");
  const [open, setOpen] = useState(false);
  const [channelInput, setChannelInput] = useState(deliveryLabel(state));

  const inputValue = isTeams ? channelInput : state.channel;
  const rawFragment = String(inputValue || "").split(/[,;\n]/).pop().trim();
  const activeFragment = isTeams && rawFragment.includes("::") ? "" : rawFragment.toLowerCase();
  const activeChannels = isTeams ? [...teamsGroups, ...teamsChannels] : slackChannels;
  const activeLoading = isTeams ? teamsLoading : slackLoading;
  const activeError = isTeams ? teamsError : slackError;
  const filteredChannels = activeFragment
    ? activeChannels.filter(ch =>
        String(ch.name || "").toLowerCase().includes(activeFragment)
        || String(ch.path || "").toLowerCase().includes(activeFragment)
        || String(ch.teamName || "").toLowerCase().includes(activeFragment)
      )
    : activeChannels;
  const filteredTeamsGroups = isTeams
    ? filteredChannels.filter(ch => ch.targetType === "group")
    : [];
  const filteredTeamsChannels = isTeams
    ? filteredChannels.filter(ch => ch.targetType === "channel")
    : [];
  const visibleSlackChannels = isSlack ? filteredChannels.slice(0, 50) : [];
  const visibleTeamsGroups = isTeams ? filteredTeamsGroups : [];
  const visibleTeamsChannels = isTeams ? filteredTeamsChannels : [];

  useEffect(() => {
    setChannelInput(deliveryLabel(state));
  }, [state.platform, state.channel, state.channelName]);

  useEffect(() => {
    let cancelled = false;

    async function loadSlackChannels() {
      if (!isSlack || slackLoaded) return;
      setSlackLoading(true);
      setSlackError("");
      const data = await fetchSlackChannels();
      if (cancelled) return;
      if (data.error) setSlackError(data.error);
      else setSlackChannels(data.channels || []);
      setSlackLoaded(true);
      setSlackLoading(false);
    }

    async function loadTeamsChannels() {
      if (!isTeams) return;
      if (teamsLoaded) return;
      setTeamsLoading(true);
      setTeamsError("");
      setTeamsNotice("");

      const data = await fetchTeamsChannels("", false);
      if (cancelled) return;

      if (data.error) {
        setTeamsError(data.error);
        setTeamsLoaded(true);
        setTeamsLoading(false);
        return;
      }

      setTeamsChannels(data.channels || []);
      setTeamsGroups(data.groups || []);
      setTeamsNotice(data.warning || "");
      setTeamsLoaded(true);
      setTeamsLoading(false);
    }

    if (isSlack) loadSlackChannels();
    else if (isTeams) loadTeamsChannels();
    else setOpen(false);

    return () => { cancelled = true; };
  }, [isSlack, isTeams, slackLoaded, teamsLoaded]);

  function selectChannel(ch) {
    const value = isTeams
      ? (ch.targetType === "group" ? `chat::${ch.id}` : `${ch.teamId || ""}::${ch.id}`)
      : ch.name;
    const label = ch.path || ch.name || value;
    let next = appendDeliveryTarget(state.channel, state.channelName, value, label);
    if (isTeams && state.channelName && /[,;\n]/.test(channelInput)) {
      const channels = String(state.channel || "").split(/[,;\n]/).map(p => p.trim()).filter(Boolean);
      const names = String(state.channelName || "").split(/[,;\n]/).map(p => p.trim()).filter(Boolean);
      if (!channels.includes(value)) {
        channels.push(value);
        names.push(label);
      }
      next = { channel: channels.join(", "), channelName: names.join(", ") };
    }
    setState(s => ({ ...s, ...next }));
    setChannelInput(isTeams ? next.channelName : next.channel);
    setOpen(false);
  }

  return (
    <div className="anim">
      <div className="step-badge">📣 Step 4 of 5 — Channel</div>
      <div className="pg-title"><span>📣</span> Delivery channel</div>
      <div className="pg-sub" style={{marginBottom:18}}>Choose where to send your automated reports.</div>
      <div className="pgrid">
        {[
          {id:"slack",name:"Slack",hint:"Post to a workspace channel",ico:"📣",bg:"rgba(78,205,196,.08)"},
          {id:"teams",name:"Microsoft Teams",hint:"Post to a Teams channel or group",ico:"🎯",bg:"rgba(98,126,234,.08)"}
        ].map(p=>(
          <div key={p.id} className={`pc ${state.platform===p.id?"on":""}`} onClick={()=>setState(s=>({...s,platform:p.id,channel:"",channelName:""}))}>
            <div className="pico" style={{background:p.bg}}>{p.ico}</div><div><div className="pn">{p.name}</div><div className="ph">{p.hint}</div></div><div className="pchk">✓</div>
          </div>
        ))}
      </div>
      <div className="flbl">Channel name</div>
      <div className="channel-wrap">
        <input
          className="cu-inp"
          style={{marginBottom:8}}
          placeholder={isSlack ? "e.g. engineering, #sales" : "Select from dropdown or paste teamId::channelId / chat::chatId"}
          value={inputValue}
          onChange={e=>{
            const value = e.target.value;
            if (isTeams) {
              setChannelInput(value);
              if (!value.trim()) {
                setState(s=>({...s,channel:"",channelName:""}));
              } else if (!state.channelName) {
                setState(s=>({...s,channel:value,channelName:""}));
              }
            } else {
              setState(s=>({...s,channel:value,channelName:value}));
            }
            if (isSlack || isTeams) setOpen(true);
          }}
          onFocus={()=> (isSlack || isTeams) && setOpen(true)}
          autoComplete="off"
        />
        {(isSlack || isTeams) && open && (
          <div className="channel-dd">
            <div className="channel-dd-head">
              <span>{isSlack ? "Slack channels and groups" : "Teams channels and groups"}</span>
              <span>{activeLoading ? "Loading" : teamsGroupLoading ? "Syncing groups" : `${filteredChannels.length} found`}</span>
            </div>
            {isTeams && teamsNotice ? (
              <div className="channel-dd-empty" style={{color:teamsNotice.toLowerCase().includes("loaded ") || teamsNotice.toLowerCase().includes("loading teams groups") ? "#475569" : "#b45309"}}>{teamsNotice}</div>
            ) : null}
            {activeError ? (
              <div className="channel-dd-empty">{activeError}</div>
            ) : activeLoading ? (
              <div className="channel-dd-empty">Loading channels...</div>
            ) : filteredChannels.length === 0 ? (
              <>
                <div className="channel-dd-empty">No matching {isSlack ? "Slack" : "Teams"} channels found.</div>
                {activeFragment ? (
                  <div className="ch-hint">
                    <span className="ch-hint-ico">i</span>
                    <span>
                      <strong>Can't find your channel?</strong>{" "}
                      {isSlack
                        ? <>Add the bot named <strong>“Clickup-reports”</strong> to your channel in Slack first, then search again.</>
                        : <>You have to add <strong>“Ankit Singh”</strong> to your group or channel for the report delivery. After adding, refresh the application once.</>}
                    </span>
                  </div>
                ) : null}
              </>
            ) : isTeams ? (
              <>
                {visibleTeamsGroups.length > 0 ? (
                  <>
                    <div className="channel-dd-section">Teams Groups ({filteredTeamsGroups.length})</div>
                    {visibleTeamsGroups.map(ch => (
                      <div
                        key={`chat-${ch.id}`}
                        className="channel-dd-item"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => selectChannel(ch)}
                      >
                        <div className="channel-dd-name">{ch.path || ch.name}</div>
                        <div className="channel-dd-meta">Team</div>
                      </div>
                    ))}
                  </>
                ) : null}
                {visibleTeamsChannels.length > 0 ? (
                  <>
                    <div className="channel-dd-section">Teams Channels ({filteredTeamsChannels.length})</div>
                    {visibleTeamsChannels.map(ch => (
                      <div
                        key={`channel-${ch.teamId || "team"}-${ch.id}`}
                        className="channel-dd-item"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => selectChannel(ch)}
                      >
                        <div className="channel-dd-name">{ch.path || ch.name}</div>
                        <div className="channel-dd-meta">{ch.isPrivate ? "Private" : "Public"}</div>
                      </div>
                    ))}
                  </>
                ) : null}
              </>
            ) : (
              <>
                {visibleSlackChannels.map(ch => (
                  <div
                    key={ch.id}
                    className="channel-dd-item"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => selectChannel(ch)}
                  >
                    <div className="channel-dd-name">{isSlack ? "#" : ""}{ch.path || ch.name}</div>
                    <div className="channel-dd-meta">{ch.targetType === "chat" ? "Group Chat" : ch.isPrivate ? "Private" : "Public"}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
      <div className="nbox"><span style={{fontSize:14,flexShrink:0}}>ℹ️</span><span>Type the <strong>exact channel name</strong> as it appears in your {isSlack?"Slack workspace":"Teams workspace"}. Separate multiple channels with commas or new lines.{isSlack?" The # prefix is okay, and Slack channels/groups will appear below.":" Teams dropdown includes both channels and group chats, and saves their IDs for reliable delivery in n8n."}</span></div>
    </div>
  );
}
// schedule step
// Schedule step 
// ─────────────────────────────────────────────────────────────────────────────
//  SCHEDULE STEP
// ─────────────────────────────────────────────────────────────────────────────
function ScheduleStep({ state, setState }) {
  const si=state.scopeItem; const m=si?TYPE_META[si.type]:null; const scopes=reportScopes(state);
  const channelLabel = deliveryLabel(state);
  return (
    <div className="anim">
      <div className="step-badge">📅 Step 5 of 5 — Schedule</div>
      <div className="pg-title"><span>📅</span> Set delivery schedule</div>
      <div className="pg-sub" style={{marginBottom:18}}>N8N triggers automatically — fetching tasks and posting the report.</div>
      <div className="flbl" style={{marginBottom:8}}>Days of the week</div>
      <div className="dgrid">{DAYS.map(d=><div key={d} className={`dp ${state.days.includes(d)?"on":""}`} onClick={()=>setState(s=>({...s,days:s.days.includes(d)?s.days.filter(x=>x!==d):[...s.days,d]}))}>{d}</div>)}</div>
      <div className="tgrid">
        <div><div className="flbl">Time</div><input type="time" className="cu-inp" value={state.time} onChange={e=>setState(s=>({...s,time:e.target.value}))}/></div>
        <div><div className="flbl">Timezone</div><div className="fwrap" style={{marginBottom:0}}><select className="cu-sel" value={state.tz} onChange={e=>setState(s=>({...s,tz:e.target.value}))}>{TIMEZONES.map(z=><option key={z} value={z}>{z}</option>)}</select><span className="sarr">▾</span></div></div>
      </div>
      <div className="sum-card">
        <div className="sum-ttl">Confirm report settings</div>
        <div className="sr"><span className="sk">Scope</span><span className="sv">{si?<span className="chip" style={{background:m.bg,color:m.color}}>{scopes.length>1?`📋 ${scopes.length} list reports`:`${m.icon} ${si.type==="List"?"List":si.type}: ${si.name}`}</span>:"—"}</span></div>
        <div className="sr"><span className="sk">Metrics</span><span className="sv"><span className="chip chip-p">… {state.metrics.length} selected</span></span></div>
        <div className="sr"><span className="sk">Channel</span><span className="sv"><span className="chip chip-b">{state.platform==="teams"?"🔷":"📣"} {channelLabel||"—"}</span></span></div>
        <div className="sr"><span className="sk">Schedule</span><span className="sv"><span className="chip chip-a">📅 {state.days.join(", ")} · {state.time} {state.tz.split("/").pop()}</span></span></div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  SIDEBAR
// ─────────────────────────────────────────────────────────────────────────────
function Sidebar({ step, state, wsName, onOpenFeedback, feedbackBusy }) {
  const channelLabel = deliveryLabel(state);
  const sub=[scopeSummary(state),state.metrics.length?`${state.metrics.length} metrics selected`:"None selected","Preview report data",state.channel?`${state.platform==="teams"?"Teams":"Slack"} · ${channelLabel}`:"Not configured",state.days.length?`${state.days.join(", ")} · ${state.time}`:"Not configured"];
  return (
    <div className="sidebar">
      <div className="sib-top"><div className="sib-ws-box"><div className="sib-ws-ico">🏢</div><div style={{flex:1,minWidth:0}}><div className="sib-ws-label">Workspace</div><div className="sib-ws-name">{wsName||"Not connected"}</div></div><span className="sib-ws-check">✓</span></div></div>
      <div className="sib-nav">
        <div className="sib-nav-ttl">Progress</div>
        {STEP_LABELS.map((lbl,i)=>(
          <div key={i} className={`srow ${i===step?"on":""}`}>
            <div className={`snum ${i<step?"done":i===step?"act":"idle"}`}>{i<step?"✓":STEP_ICONS[i]}</div>
            <div className="srow-text"><div className={`slabel ${i>step?"idle":""}`}>{lbl}</div><div className="ssub">{i<=step?sub[i]:"—"}</div></div>
            {i===step&&<span style={{width:6,height:6,borderRadius:"50%",background:"var(--p)",flexShrink:0}}/>}
            {i<step&&<span style={{fontSize:10,color:"var(--green)",fontWeight:700,flexShrink:0}}>✓</span>}
          </div>
        ))}
      </div>
      <div className="sdiv"/>
    </div>
  );
}

function AdminReportsView({ reports }) {
  const [filter, setFilter] = useState("");
  const [openOwners, setOpenOwners] = useState({});
  const query = filter.trim().toLowerCase();

  const toggleOwner = owner => {
    setOpenOwners(prev => ({ ...prev, [owner]: !prev[owner] }));
  };

  const filteredReports = reports.filter(r => {
    const owner = String(r.owner_email || "").toLowerCase();
    const scope = String(r.scope_name || "").toLowerCase();
    const path = String(r.scope_path || "").toLowerCase();
    const platform = String(r.platform || "").toLowerCase();
    const channel = String(r.channel_name || r.channel || "").toLowerCase();
    return !query || owner.includes(query) || scope.includes(query) || path.includes(query) || platform.includes(query) || channel.includes(query);
  });

  const grouped = filteredReports.reduce((acc, report) => {
    const owner = String(report.owner_email || "Unknown").trim() || "Unknown";
    if (!acc[owner]) {
      acc[owner] = { owner, reports: [], count: 0, oldestAt: null };
    }
    acc[owner].reports.push(report);
    acc[owner].count += 1;
    const createdAt = report.created_at ? new Date(report.created_at) : null;
    if (createdAt && !Number.isNaN(createdAt.getTime()) && (!acc[owner].oldestAt || createdAt < acc[owner].oldestAt)) {
      acc[owner].oldestAt = createdAt;
    }
    return acc;
  }, {});

  const groups = Object.values(grouped)
    .map(group => ({
      ...group,
      reports: group.reports.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
      firstCreatedAt: group.oldestAt,
    }))
    .sort((a, b) => b.count - a.count || a.owner.localeCompare(b.owner));

  const totalReports = filteredReports.length;
  const reporterCount = groups.length;
  const activeReports = filteredReports.filter(r => r.status === "active").length;

  return (
    <div className="anim admin-wrap">
      <div className="lh">
        <div>
          <div className="lt">Admin reports</div>
          <div className="ls">Grouped by reporter email with exact report names and running time.</div>
        </div>
        <div className="admin-search-wrap">
          <input
            className="cu-inp admin-search"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter by reporter, report name, path, channel..."
          />
        </div>
      </div>

      <div className="admin-stats">
        <div className="admin-stat"><div className="admin-stat-k">Total reports</div><div className="admin-stat-v">{totalReports}</div></div>
        <div className="admin-stat"><div className="admin-stat-k">Reporters</div><div className="admin-stat-v">{reporterCount}</div></div>
        <div className="admin-stat"><div className="admin-stat-k">Active</div><div className="admin-stat-v">{activeReports}</div></div>
      </div>

      {groups.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">🔎</div>
          <div>No reports match this filter.</div>
        </div>
      ) : (
        <div className="admin-table">
          <div className="admin-row admin-head">
            <div>Generated by</div>
          </div>
          {groups.map(group => {
            const isOpen = !!openOwners[group.owner];
            const reporterLabel = group.owner === "Unknown" ? "Email not stored" : group.owner;
            return (
              <div key={group.owner} className="admin-group">
                <div className="admin-row admin-summary">
                  <div className="admin-main">
                    <div className="admin-owner admin-owner-inline">
                      <span className="admin-email">{reporterLabel}</span>
                      <button type="button" className="admin-toggle" onClick={() => toggleOwner(group.owner)}>
                        {isOpen ? "Hide reports" : "View reports"}
                      </button>
                      <span className="admin-pill">{group.count}</span>
                    </div>
                    <div className="admin-sub">{group.reports.length} report{group.reports.length === 1 ? "" : "s"} saved</div>
                  </div>
                </div>
                {isOpen && (
                  <div className="admin-subrows">
                  <div className="admin-row admin-subhead">
                    <div>Report name</div>
                    <div>Status</div>
                    <div>Started on</div>
                    <div>Running time</div>
                    <div>Running days</div>
                  </div>
                  {group.reports.map(report => {
                    const scopeType = report.scope_type || report.type || "List";
                    const scopeName = report.scope_name || report.item || "Unknown";
                    const createdLabel = formatAdminDate(report.created_at);
                    const runningTime = elapsedLabel(report.created_at);
                    const running = daysSinceLabel(report.created_at);
                    return (
                      <div key={report.id} className="admin-row admin-subrow">
                        <div className="admin-main">
                          <div className="admin-owner">{scopeName}</div>
                          <div className="admin-sub">{scopeType}</div>
                        </div>
                        <div><span className="admin-pill muted">{report.status || "active"}</span></div>
                        <div className="admin-muted">{createdLabel}</div>
                        <div className="admin-muted">{runningTime}</div>
                        <div className="admin-muted">{running}</div>
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  SUCCESS SCREEN
// ─────────────────────────────────────────────────────────────────────────────
function SuccessScreen({ state, onAnother, onViewSaved }) {
  const si=state.scopeItem; const m=si?TYPE_META[si.type]:null; const scopes=reportScopes(state);
  const channelLabel = deliveryLabel(state);
  return (
    <div className="succ anim">
      <div className="succ-ring">✓</div>
      <div className="succ-h">Report scheduled!</div>
      <p className="succ-p">{scopes.length>1 ? <><strong>{scopes.length} list reports</strong> will be delivered as separate messages to <strong>{channelLabel}</strong></> : <>Your report for <strong>{si?.name}</strong> will be delivered to <strong>{channelLabel}</strong></>} on {state.days.join(", ")} at {state.time} {state.tz.split("/").pop()}.</p>
      <div className="succ-chips">
        {si&&<span className="chip" style={{background:m.bg,color:m.color}}>{scopes.length>1?`📋 ${scopes.length} lists`: `${m.icon} ${si.type==="List"?"List":si.type}: ${si.name}`}</span>}
        <span className="chip chip-p">… {state.metrics.length} metrics</span>
        <span className="chip chip-b">{state.platform==="teams"?"Teams":"Slack"} · {channelLabel}</span>
        <span className="chip chip-a">📅 {state.days.join(", ")} · {state.time}</span>
      </div>
      <div className="succ-btns"><button className="bg" onClick={onAnother}>← Create another</button><button className="bp" onClick={onViewSaved}>View saved reports →</button></div>
    </div>
  );
}

// Initial state 
// ─────────────────────────────────────────────────────────────────────────────
//  INITIAL STATE
// ─────────────────────────────────────────────────────────────────────────────
const INIT = () => ({
  scopeItem: null, scopeItems: [], level: null,
  metrics:   ["total","done","overdue","completion","withoutdue","withoutassignee","blocked","onhold","status","inprogress","todo"],
  platform:  "slack", channel: "", channelName: "",
  days:      ["Mon","Wed","Fri"], time: "09:00", tz: "Asia/Kolkata",
});
// Main App 
// ─────────────────────────────────────────────────────────────────────────────
//  MAIN APP
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  // Auth state — JWT in localStorage, raw token in memory + sessionStorage
  const [conn,  setConn]  = useState(null);  // { jwtToken, clickupToken, wsName, workspaces, tree }
  const [authLoading, setAuthLoading] = useState(true); // checking stored JWT on startup

  // Reports come from PostgreSQL via server — not useState
  const [saved,   setSaved]   = useState([]);
  const [savingR, setSavingR] = useState(false);
  const [savedDayFilter, setSavedDayFilter] = useState("All");
  const [editingId, setEditingId] = useState(null);
  const [editSchedule, setEditSchedule] = useState({ days:[], time:"09:00", tz:"Asia/Kolkata" });
  const [editingR, setEditingR] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [activeReportId, setActiveReportId] = useState(null);
  const savedWrapRef = useRef(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackFile, setFeedbackFile] = useState(null);
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [feedbackErr, setFeedbackErr] = useState("");

  const [tab,     setTab]     = useState("wizard");
  const [step,    setStep]    = useState(0);
  const [state,   setState]   = useState(INIT());
  const [valErr,  setValErr]  = useState("");
  const filteredSavedReports = saved.filter(report => {
    if (savedDayFilter === "All") return true;
    return reportDays(report).includes(savedDayFilter);
  });

  useEffect(() => {
    if (tab !== "list") { setActiveReportId(null); return; }

    let frameId = 0;
    const updateActiveCard = () => {
      frameId = 0;
      const cards = Array.from(savedWrapRef.current?.querySelectorAll("[data-report-card]") || []);
      if (!cards.length) { setActiveReportId(null); return; }

      const viewportCenterY = window.innerHeight * 0.52;
      const viewportCenterX = window.innerWidth / 2;
      let best = null;
      let bestScore = Infinity;

      for (const card of cards) {
        const rect = card.getBoundingClientRect();
        const visibleY = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        if (visibleY <= 0) continue;

        const cardCenterY = rect.top + rect.height / 2;
        const cardCenterX = rect.left + rect.width / 2;
        const score = Math.abs(cardCenterY - viewportCenterY) + Math.abs(cardCenterX - viewportCenterX) * 0.18;
        if (score < bestScore) {
          bestScore = score;
          best = card.dataset.reportId;
        }
      }

      setActiveReportId(best);
    };
    const scheduleActiveCardUpdate = () => {
      if (!frameId) frameId = window.requestAnimationFrame(updateActiveCard);
    };

    const savedEl = savedWrapRef.current;
    scheduleActiveCardUpdate();
    window.addEventListener("scroll", scheduleActiveCardUpdate, { passive: true });
    window.addEventListener("resize", scheduleActiveCardUpdate);
    savedEl?.addEventListener("scroll", scheduleActiveCardUpdate, { passive: true });

    return () => {
      window.removeEventListener("scroll", scheduleActiveCardUpdate);
      window.removeEventListener("resize", scheduleActiveCardUpdate);
      savedEl?.removeEventListener("scroll", scheduleActiveCardUpdate);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [tab, saved.length]);

  // On startup: check if JWT still valid — auto-login without re-entering token
  useEffect(() => {
    async function tryAutoLogin() {
      const jwt          = localStorage.getItem(JWT_KEY);
      const clickupToken = sessionStorage.getItem(CLICKUP_KEY);
      if (!jwt) { setAuthLoading(false); return; }
      try {
        const res  = await fetch(`${SERVER}/api/auth/refresh`, {
          method:"POST", headers:{"Authorization":`Bearer ${jwt}`}
        });
        const data = await res.json();
        if (!data.success) { localStorage.removeItem(JWT_KEY); setAuthLoading(false); return; }
        if (!data.email) {
          // Force fresh Google sign-in for older non-Google sessions.
          localStorage.removeItem(JWT_KEY);
          sessionStorage.removeItem(CLICKUP_KEY);
          setAuthLoading(false);
          return;
        }
        // JWT valid — refresh it and restore session
        localStorage.setItem(JWT_KEY, data.jwtToken);
        const headers = { "Authorization": `Bearer ${data.jwtToken}` };
        if (clickupToken) headers["x-clickup-token"] = clickupToken;
        const wsRes  = await fetch(`${SERVER}/api/workspace?teamId=${data.workspaceId||""}`, { headers }).catch(()=>null);
        const wsData = wsRes ? await wsRes.json().catch(()=>({})) : {};
      setConn({
        jwtToken: data.jwtToken,
        clickupToken: clickupToken || null,
        wsName: data.wsName,
        workspaces: data.workspaceId ? [{ id:data.workspaceId, name:data.wsName }] : [],
        tree: wsData.tree || [],
        email: data.email || "",
        userName: data.userName || "",
        isAdmin: !!data.isAdmin,
        isDemo: false,
      });
        // Load saved reports from PostgreSQL
        await loadReports(data.jwtToken);
      } catch {}
      setAuthLoading(false);
    }
    tryAutoLogin();
  }, []);

  // Load reports from PostgreSQL
  async function loadReports(jwt) {
    try {
      const res  = await fetch(`${SERVER}/api/reports`, { headers:{"Authorization":`Bearer ${jwt||localStorage.getItem(JWT_KEY)}`} });
      const data = await res.json();
      if (data.reports) setSaved(data.reports.filter(r=>r.status!=="deleted"));
    } catch {}
  }

  const reset  = () => { setStep(0); setState(INIT()); setValErr(""); };
  const goList = () => { setTab("list"); loadReports(); };
  const goAdmin = () => { setTab("admin"); loadReports(); };

  function activateDemoMode() {
    setConn({ jwtToken:null, clickupToken:null, wsName:"Demo Workspace", workspaces:[{id:"demo",name:"Demo Workspace"}], tree:[{id:"sp1",name:"Engineering",type:"Space",parent:null,path:"Engineering"},{id:"fo1",name:"Q3 Sprint",type:"Folder",parent:"sp1",path:"Engineering > Q3 Sprint"},{id:"li1",name:"Website Redesign",type:"List",parent:"fo1",path:"Engineering > Q3 Sprint > Website Redesign",taskCount:41},{id:"li2",name:"Mobile App v2",type:"List",parent:"fo1",path:"Engineering > Q3 Sprint > Mobile App v2",taskCount:28}], email:"demo@example.com", userName:"Demo User", isAdmin:false, isDemo:true });
    setAuthLoading(false);
  }

  async function onConnected(data) {
    if (!data) { activateDemoMode(); return; }
    setConn({ ...data, isAdmin: !!data.isAdmin, isDemo: !!data.isDemo });
    await loadReports(data.jwtToken);
    setAuthLoading(false);
  }

  function onDisconnect() {
    localStorage.removeItem(JWT_KEY);
    sessionStorage.removeItem(CLICKUP_KEY);
    setConn(null); setSaved([]); setEditingId(null); reset();
  }

  function openFeedbackModal() {
    setFeedbackErr("");
    setFeedbackMsg("");
    setFeedbackFile(null);
    setFeedbackOpen(true);
  }

  function closeFeedbackModal() {
    if (feedbackBusy) return;
    setFeedbackOpen(false);
    setFeedbackFile(null);
  }

  async function submitFeedback() {
    const text = feedback.trim();
    const file = feedbackFile;
    if (!text) {
      setFeedbackErr("Please write a short feedback message.");
      return;
    }
    setFeedbackBusy(true);
    setFeedbackErr("");
    setFeedbackMsg("");
    try {
      let attachment = null;
      if (file) {
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error("Unable to read the selected file."));
          reader.readAsDataURL(file);
        });
        attachment = {
          filename: file.name,
          contentType: file.type || "application/octet-stream",
          content: String(fileData).split(",")[1] || "",
        };
      }

      const res = await apiRequest("/api/feedback", {
        method: "POST",
        body: JSON.stringify({
          feedback: text,
          userEmail: conn?.email || "",
          userName: conn?.userName || conn?.email || "",
          attachment,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        const detailMsg = data.details ? ` ${data.details}` : "";
        setFeedbackErr((data.error || "Failed to send feedback.") + detailMsg);
        return;
      }
      setFeedback("");
      setFeedbackFile(null);
      setFeedbackMsg("Thanks. Your feedback was sent.");
    } catch (e) {
      setFeedbackErr(e.message || "Failed to send feedback.");
    } finally {
      setFeedbackBusy(false);
    }
  }

  const canNext = () => {
    if (step===0) {
      const scopes = reportScopes(state);
      if (scopes.length === 0) return false;
      if (scopes.length === 1) return true;
      // Multi: all must be same type (List up to 3, Folder up to 4), all in same Space
      const allList   = scopes.every(i => i.type === "List");
      const allFolder = scopes.every(i => i.type === "Folder");
      if (allList   && scopes.length <= 3) return true;
      if (allFolder && scopes.length <= 4) return true;
      return false;
    }
    if (step===1) return state.metrics.length > 0;
    if (step===2) return true;
    if (step===3) return !!state.channel.trim();
    return state.days.length > 0;
  };
  const STEP_ERR = ["Please select a Space, up to 4 Folders, or up to 3 Lists from one Space.","Please select at least one metric.","","Please enter a channel name.","Please select at least one day."];

  function nextStep() { if (!canNext()) { setValErr(STEP_ERR[step]); return; } setValErr(""); setStep(s=>s+1); }
  function prevStep() { setValErr(""); setStep(s=>s-1); }

  // Save to PostgreSQL — reports survive refresh, server restarts, everything
  async function saveReport() {
    if (!canNext()) { setValErr(STEP_ERR[step]); return; }
    setSavingR(true);
    const scopes = reportScopes(state);
    const channel = state.channel.trim();
    const channelName = (state.channelName || channel).trim();
    try {
      // Demo mode — just save locally
      if (!conn?.jwtToken) {
        setSaved(prev=>[...prev,...scopes.map((si,index)=>({id:Date.now()+index,scope_name:si.name,scope_type:si.type,metrics:state.metrics,platform:state.platform,channel,channel_name:channelName,days:state.days,time:state.time,timezone:state.tz,status:"active",owner_email:conn?.email || "demo@example.com"}))]);
        setStep(5); return;
      }
      for (const si of scopes) {
        const res  = await apiRequest("/api/reports", {
          method:"POST",
          body: JSON.stringify({
            scopeType: si.type, scopeId: si.id, scopeName: si.name, scopePath: si.path,
            metrics: state.metrics, platform: state.platform, channel, channelName,
            days: state.days, time: state.time, timezone: state.tz,
          }),
        }, conn.clickupToken);
        const data = await res.json();
        if (!data.success) { setValErr("Failed to save: " + (data.error||"Unknown error")); return; }
      }
      await loadReports();
      setStep(5);
    } catch (e) {
      setValErr("Server error: " + e.message);
    } finally {
      setSavingR(false);
    }
  }

  async function deleteReport(id) {
    if (!conn?.jwtToken) { setSaved(p=>p.filter(x=>x.id!==id)); return; }
    await apiRequest(`/api/reports/${id}`, { method:"DELETE" });
    await loadReports();
  }

  async function pauseReport(id) {
    if (!conn?.jwtToken) { setSaved(p=>p.map(r=>r.id===id?{...r,status:r.status==="active"?"paused":"active"}:r)); return; }
    await apiRequest(`/api/reports/${id}/pause`, { method:"PATCH" });
    await loadReports();
  }

  function beginEditReport(report) {
    setEditingId(report.id);
    setEditSchedule({
      days: reportDays(report),
      time: reportTime(report),
      tz: report.timezone || report.tz || "Asia/Kolkata",
    });
    setEditErr("");
  }

  function toggleEditDay(day) {
    setEditSchedule(s => ({
      ...s,
      days: s.days.includes(day) ? s.days.filter(d => d !== day) : [...s.days, day],
    }));
  }

  function cancelEditReport() {
    setEditingId(null);
    setEditErr("");
  }

  async function updateReportSchedule(id) {
    if (!editSchedule.days.length) { setEditErr("Please select at least one delivery day."); return; }
    setEditingR(true);
    setEditErr("");
    try {
      if (!conn?.jwtToken) {
        setSaved(p => p.map(r => r.id===id ? {
          ...r,
          days: editSchedule.days,
          time: editSchedule.time,
          timezone: editSchedule.tz,
        } : r));
        cancelEditReport();
        return;
      }
      const res = await apiRequest(`/api/reports/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          days: editSchedule.days,
          time: editSchedule.time,
          timezone: editSchedule.tz,
        }),
      });
      const data = await res.json();
      if (!data.success) { setEditErr(data.error || "Failed to update schedule."); return; }
      await loadReports();
      cancelEditReport();
    } catch (e) {
      setEditErr("Server error: " + e.message);
    } finally {
      setEditingR(false);
    }
  }

  const cuClass = "cu light";

  if (authLoading) return (
    <div className={cuClass}><StyleInjector/>
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",gap:12,fontSize:13,color:"var(--tm)"}}>
        <div className="spinner"/><span>Loading…</span>
      </div>
    </div>
  );

  if (!conn) return (
    <div className={cuClass}><StyleInjector/>
      <div className="topbar"><div className="tl"/><div className="tcenter"><span className="tname">Pragati-AI</span></div><div className="tright"/></div>
      <ConnectionGate onConnected={onConnected}/>
    </div>
  );

  const STEPS = [
    <ScopeStep    state={state} setState={setState} conn={conn}/>,
    <MetricsStep  state={state} setState={setState}/>,
    <PreviewStep  state={state} conn={conn}/>,
    <ChannelStep  state={state} setState={setState}/>,
    <ScheduleStep state={state} setState={setState}/>,
  ];

  return (
    <div className={cuClass}><StyleInjector/>
      <div className="topbar">
        <div className="tl"><span className="twsname">{conn.wsName}</span></div>
        <div className="tcenter"><span className="tname">Pragati-AI</span></div>
        <div className="tright">
          <button className="new-btn" onClick={()=>{reset();setTab("wizard");}}>+ New report</button>
          <button className="fb-open-btn topbar-fb" onClick={openFeedbackModal} disabled={feedbackBusy}>Send feedback</button>
          <button className="disc-btn" onClick={onDisconnect}>Log out</button>
        </div>
      </div>
      <div className="tabs">
        <button className={`tab ${tab==="wizard"?"on":""}`} onClick={()=>setTab("wizard")}>✦ Configure</button>
        <button className={`tab ${tab==="list"?"on":""}`}   onClick={goList}>📋 Saved {saved.length>0&&<span className="tcnt">{saved.length}</span>}</button>
        {conn?.isAdmin && <button className={`tab ${tab==="admin"?"on":""}`} onClick={goAdmin}>🛡 Admin</button>}
      </div>

      {tab==="wizard"&&(
        <div className="layout">
          <Sidebar step={Math.min(step,4)} state={state} wsName={conn.wsName} onOpenFeedback={openFeedbackModal} feedbackBusy={feedbackBusy}/>
          <div className="main">
            {step>=5 ? <SuccessScreen state={state} onAnother={reset} onViewSaved={goList}/> : (
              <>
                {valErr&&<div className="val-err show">✗  {valErr}</div>}
                {STEPS[step]}
                <div className="sfoot">
                  {step>0?<button className="bg" onClick={prevStep}>← Back</button>:<span/>}
                  {step<4
                    ? <button className="bp" onClick={nextStep}>Continue →</button>
                    : <button className="bs" onClick={saveReport} disabled={savingR}>{savingR?<><span className="spinner wh sm"/>Saving…</>:reportScopes(state).length>1?"💾 Save reports":"💾 Save report"}</button>
                  }
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {feedbackOpen && (
        <div className="fb-modal-backdrop" onClick={closeFeedbackModal}>
          <div className="fb-modal" onClick={e => e.stopPropagation()}>
            <div className="fb-modal-top">
              <div>
                <div className="fb-modal-title">Send feedback</div>
              </div>
              <button className="fb-close" onClick={closeFeedbackModal} disabled={feedbackBusy}>×</button>
            </div>
            <div className="fb-modal-body">
              <textarea
                className="fb-ta"
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                placeholder="Write your feedback here..."
              />
              <div style={{marginTop:10}}>
                <label className="gate-lbl" style={{marginBottom:6}}>Attachment</label>
                <input
                  type="file"
                  accept="image/*,.png,.jpg,.jpeg,.webp,.gif,.pdf"
                  className="cu-inp"
                  style={{padding:"9px 11px"}}
                  onChange={e => setFeedbackFile(e.target.files?.[0] || null)}
                  disabled={feedbackBusy}
                />
                <div style={{fontSize:11,color:"var(--th)",marginTop:6}}>
                  Optional. You can attach a screenshot, image, or PDF.
                </div>
                {feedbackFile && (
                  <div style={{fontSize:11,color:"var(--p)",marginTop:6,fontWeight:600}}>
                    Selected: {feedbackFile.name}
                  </div>
                )}
              </div>
              {feedbackErr && <div className="feedback-err">{feedbackErr}</div>}
              {feedbackMsg && <div className="feedback-ok">{feedbackMsg}</div>}
              <div className="fb-modal-actions">
                <button className="fb-secondary" onClick={closeFeedbackModal} disabled={feedbackBusy}>Cancel</button>
                <button className="fb-primary" onClick={submitFeedback} disabled={feedbackBusy}>
                  {feedbackBusy ? "Sending…" : "Send feedback"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab==="list"&&(
        <div ref={savedWrapRef} className="saved-wrap">
          <div className="lh">
            <div>
              <div className="lt">Saved reports</div>
              <div className="ls">
                {filteredSavedReports.length} of {saved.length} report{saved.length!==1?"s":""} shown
                {conn?.isAdmin ? " · admin view" : ""}
              </div>
            </div>
            <button className="new-btn" onClick={()=>{reset();setTab("wizard");}}>+ New report</button>
          </div>
          <div className="saved-day-filter" role="tablist" aria-label="Filter reports by scheduled day">
            <button type="button" className={`saved-day-chip ${savedDayFilter==="All"?"on":""}`} onClick={()=>setSavedDayFilter("All")}>All</button>
            {DAYS.map(day => (
              <button
                key={day}
                type="button"
                className={`saved-day-chip ${savedDayFilter===day?"on":""}`}
                onClick={()=>setSavedDayFilter(day)}
              >
                {day}
              </button>
            ))}
          </div>
          {saved.length===0 ? (
            <div className="empty"><div className="empty-icon">📋</div><div>No saved reports yet.</div><div style={{fontSize:11,marginTop:5,color:"var(--th)"}}>Create your first report to get started.</div></div>
          ) : filteredSavedReports.length===0 ? (
            <div className="empty"><div className="empty-icon">🔎</div><div>No reports scheduled on {savedDayFilter}.</div><div style={{fontSize:11,marginTop:5,color:"var(--th)"}}>Try another day or switch back to All.</div></div>
          ) : (
            <div className="rcards">{filteredSavedReports.map(r=>{
              const mt=TYPE_META[r.scope_type||r.type]||TYPE_META["List"];
              const isActive=r.status==="active";
              const metrics=Array.isArray(r.metrics)?r.metrics.length:r.metrics;
              const daysArr=reportDays(r);
              const days=daysArr.join(", ");
              const channelLabel=deliveryLabel(r);
              const isEditing=editingId===r.id;
              return (
                <div key={r.id} data-report-card data-report-id={r.id} className={`rcard ${String(activeReportId)===String(r.id)?"is-scroll-active":""}`}>
                  <div className="rhero" style={{background:`linear-gradient(135deg, ${mt.bg}, rgba(255,255,255,.58))`}}>
                    <div className="rhero-ico">{mt.icon}</div>
                    <span className={`sbadge ${isActive?"act":"pause"}`}><span className="sdot"/>{isActive?"Active":"Paused"}</span>
                  </div>
                  <div className="rbody">
                    <div className="rb">
                      <div className="rn">{r.scope_name||r.item}</div>
                      <div className="rm">
                        <div className="rbox">
                          <div className="rbox-k">Schedule</div>
                          <div className="rbox-v">📅 {days || "No days"} · {r.time} {(r.timezone||"Asia/Kolkata").split("/").pop()}</div>
                        </div>
                        <div className="rbox">
                          <div className="rbox-k">Delivery</div>
                          <div className="rbox-v">{r.platform==="teams"?"🔷 Teams":"📣 Slack"} · {channelLabel || "Not set"}</div>
                        </div>
                        <div className="rbox">
                          <div className="rbox-k">Report</div>
                          <div className="rbox-v">{(r.scope_type||r.type)==="List"?"List":(r.scope_type||r.type)} · {metrics} metrics</div>
                        </div>
                        <div className="rbox">
                          <div className="rbox-k">Owner</div>
                          <div className="rbox-v">{r.owner_email || conn?.email || "Unknown"}</div>
                        </div>
                      </div>

                      {isEditing&&(
                        <div className="edit-panel">
                          <div className="edit-grid">
                            <div>
                              <div className="flbl">Days</div>
                              <div className="mini-days">{DAYS.map(d=><button key={d} type="button" className={`mini-day ${editSchedule.days.includes(d)?"on":""}`} onClick={()=>toggleEditDay(d)}>{d}</button>)}</div>
                            </div>
                            <div>
                              <div className="flbl">Time</div>
                              <input type="time" className="cu-inp" value={editSchedule.time} onChange={e=>setEditSchedule(s=>({...s,time:e.target.value}))}/>
                            </div>
                            <div>
                              <div className="flbl">Timezone</div>
                              <div className="fwrap" style={{marginBottom:0}}><select className="cu-sel" value={editSchedule.tz} onChange={e=>setEditSchedule(s=>({...s,tz:e.target.value}))}>{TIMEZONES.map(z=><option key={z} value={z}>{z}</option>)}</select><span className="sarr">▾</span></div>
                            </div>
                          </div>
                          {editErr&&<div className="edit-err">✗ {editErr}</div>}
                          <div className="edit-actions">
                            <button className="bg" onClick={cancelEditReport} disabled={editingR}>Cancel</button>
                            <button className="bs" onClick={()=>updateReportSchedule(r.id)} disabled={editingR}>{editingR?<><span className="spinner wh sm"/>Saving…</>:"Save schedule"}</button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="ract">
                      <button className="ia" onClick={()=>isEditing?cancelEditReport():beginEditReport(r)}>{isEditing?"Close":"✎ Edit"}</button>
                      <button className="ia" onClick={()=>pauseReport(r.id)}>{isActive?"⏸":"▶"}</button>
                      <button className="ia" onClick={()=>deleteReport(r.id)}>🗑</button>
                    </div>
                  </div>
                </div>
              );
            })}</div>
          )}
        </div>
      )}

      {tab==="admin" && conn?.isAdmin && (
        <div className="saved-wrap">
          <AdminReportsView reports={saved} />
        </div>
      )}
    </div>
  );
}
